import React from "react";
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection,
  collectionGroup,
  doc,
  deleteDoc,
  getDoc,
  getDocFromServer,
  getDocs,
  limit,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { freeLocalStorageForCriticalWrite, idbRemoveItem, idbSetItem, initializeMorpheusStorageEngine, shouldUseIndexedDb } from "./mcStorageEngine";
import { recordMcTrafficAckMismatch, recordMcTrafficFirestoreDelete, recordMcTrafficFirestoreRead, recordMcTrafficFirestoreWrite, recordMcTrafficWriteAttempt, recordMcTrafficWriteFailure } from "./mcTrafficProbe";

const MC_SYNC_EVENT = "mc-cloud-sync";
const MC_SYNC_READY_EVENT = "mc-cloud-sync-ready";

let activeOrgId = "";
const volatileMcCache = new Map();
let activeUser = { email: "", uid: "" };
let unsubscribeSync = null;
let bridgeInstalled = false;
let originalSetItem = null;
let originalRemoveItem = null;
let resolvingOrgPromise = null;
let applyingCloud = false;
let activeCleanBootstrap = false;
let activeSnapshotHydrated = false;
const cleanBootstrapPurgedOrgs = new Set();
const pendingWrites = new Map();
const recentLocalWrites = new Map();
// Canonical removal markers survive stale tabs and the legacy People importer.
let removedProfileIds = new Set();
let profileWriteChain = Promise.resolve();
// MC428 — Browser storage can be written many times during a single React render/
// projection pass. Previously every localStorage.setItem immediately created a
// Firestore write. Under live sessions this could enqueue hundreds of writes,
// exhausting Firestore's WebChannel stream and making unrelated Verify actions
// appear to hang forever. Background bridge writes are now globally serialized
// and coalesced by key; lifecycle actions still use their explicit transactional
// APIs and are never routed through this queue.
const scheduledWriteValues = new Map();
let scheduledWritePumpRunning = false;
let scheduledWriteResumeTimer = null;
let scheduledWriteFailureStreak = 0;
const RECENT_LOCAL_WRITE_MS = 120000;

const LOCAL_ONLY_KEYS = new Set([
  "mc-current-profile",
  "mc-current-user-email",
  "mc-admin-store-view",
  // Theme must stay device-local. MC data is shared through Firestore, but each
  // browser/device should keep its own light/dark preference.
  "mc-theme",
  "mc-session-list-lock",
]);

const ORG_SESSION_KEY = "mc-active-org-id";
const ORG_CACHE_VERSION_KEY = "mc-active-org-cache-version";
const ORG_CACHE_VERSION = "mc-org-scope-v43-authoritative-site-manifest";

function isSafariIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (/Safari/.test(ua) && !/Chrome|CriOS|Edg/.test(ua));
}

function collectMcCacheKeysFromStore(store) {
  const keys = [];
  if (!store) return keys;
  try {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i) || "";
      if (key === "mc-theme") continue;
      if (isMcKey(key) || key === "selectedStore" || key === "selectedSite" || key === "morpheus-selected-site") keys.push(key);
    }
  } catch (_) {}
  return keys;
}

function clearMcLocalStorageForOrgSwitch(nextOrgId = "") {
  if (typeof window === "undefined") return;
  const cleanNext = String(nextOrgId || "").trim();

  // Hard org isolation: localStorage/sessionStorage are cache only. Clear shared
  // MC data from BOTH stores before the next org snapshot is applied so a fresh
  // or demo org never renders another org's dashboard, archives, sites, billing,
  // categories, session fallbacks, or profile list while Firestore is hydrating.
  const localKeys = collectMcCacheKeysFromStore(window.localStorage);
  const sessionKeys = collectMcCacheKeysFromStore(window.sessionStorage);
  const keys = Array.from(new Set([...localKeys, ...sessionKeys]));

  applyingCloud = true;
  try {
    // Memory caches are process-wide and survive logout/login inside the SPA.
    // They must be cleared before the next organisation is allowed to hydrate,
    // otherwise mcGetItem/active inventory can return the previous org even
    // after localStorage and sessionStorage were purged.
    volatileMcCache.clear();
    manifestContentCache.clear();
    manifestContentInflight.clear();
    pendingWrites.clear();
    scheduledWriteValues.clear();
    scheduledWriteFailureStreak = 0;
    if (scheduledWriteResumeTimer) { clearTimeout(scheduledWriteResumeTimer); scheduledWriteResumeTimer = null; }
    recentLocalWrites.clear();
    removedProfileIds.clear();
    try {
      if (window.__MC_ACTIVE_INVENTORY__) window.__MC_ACTIVE_INVENTORY__ = Object.create(null);
      if (window.__MC_ACTIVE_IMPORT_SNAPSHOTS__) window.__MC_ACTIVE_IMPORT_SNAPSHOTS__ = Object.create(null);
    } catch (_) {}

    keys.forEach((key) => {
      try {
        if (key !== "mc-theme" && !isLocalOnlyMcKey(key)) {
          if (originalRemoveItem) originalRemoveItem.call(window.localStorage, key);
          else window.localStorage.removeItem(key);
        }
      } catch (_) {}
      try {
        if (!isLocalOnlyMcKey(key)) window.sessionStorage?.removeItem(key);
      } catch (_) {}
    });
    try { window.sessionStorage?.removeItem('mc-access-layer-unlocked'); } catch (_) {}
    try { window.sessionStorage?.setItem(ORG_SESSION_KEY, cleanNext); } catch (_) {}
    try { window.sessionStorage?.setItem(ORG_CACHE_VERSION_KEY, ORG_CACHE_VERSION); } catch (_) {}
  } finally {
    applyingCloud = false;
  }
}

function isLocalOnlyMcKey(key = "") {
  const clean = String(key || "");
  return LOCAL_ONLY_KEYS.has(clean) || clean.startsWith("mc-latest-import-lock:") || clean.startsWith("mc-active-import-lock:");
}

const KEY_TO_DOC = {
  "mc-profiles": "profiles",
  "mc-sections": "sections",
  "mc-cycles": "cycles",
  "mc-current-profile": "currentProfile",
  "mc-admin-store-view": "adminStoreView",
  "mc-logic-pass": "engine_ALL",
  "mc-import-active": "import_ALL",
  "mc-session-counter": "sessionCounter",
  "mc-session-seeds": "prepared",
  "mc-session-list": "sessions",
  "mc-session-records": "sessions",
  "mc-archive-records": "archives",
  "mc-session-archive": "archives",
};

function isMcKey(key = "") {
  return String(key || "").startsWith("mc-");
}

function isCriticalSessionKey(key = "") {
  const clean = String(key || "");
  return clean === "mc-role-escalation-policy-v1"
    || clean === "mc-session-list"
    || clean === "mc-session-archive"
    || /^mc-archive-session:/i.test(clean)
    || /^mc-session-engine[:\-]/i.test(clean)
    || /^mc-session-checkpoint:/i.test(clean)
    || /^mc-count-ledger:/i.test(clean)
    || /^mc-session-completed:/i.test(clean);
}

// MC429 — canonical lifecycle documents have dedicated durable writers. Routing
// the same value through the generic localStorage bridge as well creates a second
// Firestore transaction against the exact same document. That duplicate writer
// was the source of the 409/failed-precondition retry storm that made Verify and
// Finalize take close to a minute even though they eventually succeeded.
function isExplicitCloudOwnedKey(key = "") {
  const clean = String(key || "");
  return clean === "mc-session-list"
    || clean === "mc-session-archive"
    || clean === "mc-session-list-backup"
    || /^mc-count-ledger:/i.test(clean)
    || /^mc-final-count-snapshot:/i.test(clean)
    // MC431 — compatibility/session-detail mirrors also have dedicated writers.
    // Letting the generic localStorage bridge write the same document creates a
    // second transactional writer and can saturate Firestore's write stream while
    // Verify/Finalize are waiting for their canonical lifecycle transaction.
    || /^mc-session-engine[:\-]/i.test(clean)
    || /^mc-session-checkpoint:/i.test(clean)
    || /^mc-session-completed:/i.test(clean)
    || /^mc-archive-session:/i.test(clean)
    || /^mc-session-detail:/i.test(clean);
}

function safeJsonParse(raw, fallback) {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) {
    return fallback;
  }
}

function normalizeImportScopeValue(value = "ALL") {
  const raw = String(value || "ALL").trim();
  if (!raw || raw.toUpperCase() === "ALL") return "ALL";
  const withoutPrefix = raw.replace(/^inventory_/i, "");
  const cleaned = withoutPrefix
    .replace(/_Store$/i, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!cleaned) return "ALL";
  const titled = cleaned
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("_");
  return `inventory_${titled}`;
}

function importScopeLockCandidates(scope = "ALL") {
  const normalized = normalizeImportScopeValue(scope);
  const raw = String(scope || "ALL").trim() || "ALL";
  const label = normalized === "ALL" ? "ALL" : normalized.replace(/^inventory_/i, "").replace(/_/g, " ");
  return Array.from(new Set([
    normalized,
    raw,
    label,
    String(normalized || "").toLowerCase(),
    String(raw || "").toLowerCase(),
    String(label || "").toLowerCase(),
    String(normalized || "").toUpperCase(),
    String(raw || "").toUpperCase(),
  ].filter(Boolean)));
}

function isLegacySeededEnginePayload(localKey = "", data = {}) {
  const key = String(localKey || "");
  if (key !== "mc-logic-pass" && !/^mc-engine:?/i.test(key)) return false;
  const payload = data && data.json !== undefined ? data.json : safeJsonParse(data?.value, null);
  if (!payload || typeof payload !== "object") return false;

  const hasNoImportedData = !String(payload.csvText || "").trim()
    && (!Array.isArray(payload.rawRows) || payload.rawRows.length === 0)
    && (!Array.isArray(payload.preparedRows) || payload.preparedRows.length === 0);
  if (!hasNoImportedData) return false;

  const rules = Array.isArray(payload.rules) ? payload.rules : [];
  const groups = Array.isArray(payload.config?.categoryGroups) ? payload.config.categoryGroups : [];
  const order = Array.isArray(payload.config?.categoryOrder) ? payload.config.categoryOrder : [];

  // Old bootstraps wrote The Spot/demo category rules into brand-new orgs.
  // For clean-bootstrap orgs, remove those empty seeded docs so the org starts clean.
  const looksLikeDemoCategorySeed = rules.length >= 10 || groups.length >= 3 || order.length >= 10;
  return looksLikeDemoCategorySeed;
}

function normalizeProfileRole(profile = {}) {
  return String(profile?.role || '').trim().toLowerCase();
}

function profilesFromStoredValue(value) {
  const parsed = typeof value === 'string' ? safeJsonParse(value, []) : value;
  if (Array.isArray(parsed)) return parsed.filter(Boolean);
  if (Array.isArray(parsed?.profiles)) return parsed.profiles.filter(Boolean);
  if (Array.isArray(parsed?.items)) return parsed.items.filter(Boolean);
  if (Array.isArray(parsed?.value)) return parsed.value.filter(Boolean);
  return [];
}


function getTrustedCachedProfilesState() {
  if (typeof window === "undefined") return { ok: false, count: 0 };
  let profiles = [];
  try {
    profiles = profilesFromStoredValue(window.localStorage?.getItem("mc-profiles") || "");
  } catch (_) {
    profiles = [];
  }

  if (!Array.isArray(profiles) || profiles.length === 0) return { ok: false, count: 0 };

  const nonAdmins = profiles.filter((profile) => normalizeProfileRole(profile) !== "admin");
  const fallbackOnlyAdmin = profiles.length === 1
    && normalizeProfileRole(profiles[0]) === "admin"
    && String(profiles[0]?.pin || "") === "000000";

  // A warm browser refresh may reuse a previously hydrated real profile list,
  // but it must never unlock from the generated fallback admin alone.
  const ok = Boolean(!fallbackOnlyAdmin && (nonAdmins.length > 0 || profiles.some((profile) => String(profile?.pin || "") !== "000000")));
  return { ok, count: profiles.length };
}

function isDangerousProfilesOverwrite(nextProfiles = [], existingProfiles = []) {
  if (!Array.isArray(nextProfiles) || !Array.isArray(existingProfiles)) return false;
  if (!existingProfiles.length) return false;

  const nextNonAdmins = nextProfiles.filter((profile) => normalizeProfileRole(profile) !== 'admin');
  const existingNonAdmins = existingProfiles.filter((profile) => normalizeProfileRole(profile) !== 'admin');

  // Fresh/private browsers can briefly create only the fallback admin before the
  // Firestore snapshot arrives. Never allow that local fallback to replace the
  // real org profile list in the cloud.
  if (!nextNonAdmins.length && existingNonAdmins.length) return true;
  if (nextProfiles.length < existingProfiles.length && !nextNonAdmins.length) return true;

  const nextAdmin = nextProfiles.find((profile) => normalizeProfileRole(profile) === 'admin');
  const existingAdmin = existingProfiles.find((profile) => normalizeProfileRole(profile) === 'admin');
  if (nextAdmin && existingAdmin) {
    const nextPin = String(nextAdmin.pin || '');
    const existingPin = String(existingAdmin.pin || '');
    if (nextPin === '000000' && existingPin && existingPin !== '000000' && !nextNonAdmins.length) return true;
  }

  return false;
}

export function getMcRemovedProfileIds() {
  return new Set(removedProfileIds);
}

// All Directory writes share one transaction path. Generic saves remain unable
// to erase people; only this explicit removal operation may reduce the list.
async function mutateProfileAuthority(incomingValue, removeId = "") {
  const orgId = activeOrgId;
  if (!orgId || !activeSnapshotHydrated) throw new Error("People are still syncing. Try again once syncing finishes.");
  const ref = morpheusDocRef(orgId, "profiles");
  let committed = null;
  let legacyPeople = null;
  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(ref);
    let data = snapshot.exists() ? snapshot.data() || {} : {};
    if (!snapshot.exists()) {
      const legacy = await transaction.get(morpheusDocRef(orgId, "mc-profiles"));
      data = legacy.exists() ? legacy.data() || {} : {};
    }
    const removed = new Set(Array.isArray(data.removedProfileIds) ? data.removedProfileIds : []);
    const existing = profilesFromStoredValue(data.value || data.json).filter((item) => !removed.has(item.id));
    let next;
    if (removeId) {
      const target = existing.find((item) => item.id === removeId);
      if (target?.isPrimaryOwner === true || target?.primaryOwner === true) throw new Error("The Primary Owner cannot be removed.");
      const peopleRef = morpheusDocRef(orgId, "response-people");
      const peopleSnapshot = await transaction.get(peopleRef);
      const peopleData = peopleSnapshot.exists() ? peopleSnapshot.data() || {} : {};
      const people = profilesFromStoredValue(peopleData.value || peopleData.json);
      const identity = (item) => String(item?.fullName || [item?.name, item?.surname].filter(Boolean).join(" ")).trim().toLowerCase();
      const email = String(target?.email || "").trim().toLowerCase();
      const name = identity(target);
      const uniqueName = name && existing.filter((item) => identity(item) === name).length === 1 && people.filter((item) => identity(item) === name).length === 1;
      const matches = (item) => item.id === removeId
        || Boolean(email && String(item.email || "").trim().toLowerCase() === email)
        || Boolean(uniqueName && (!email || !String(item.email || "").trim()) && identity(item) === name);
      removed.add(removeId);
      people.filter(matches).forEach((person) => removed.add(person.id));
      next = existing.filter((item) => !removed.has(item.id));
      legacyPeople = people.filter((item) => !matches(item));
      if (legacyPeople.length !== people.length) {
        transaction.set(peopleRef, { localKey: "mc-response-people", value: JSON.stringify(legacyPeople), json: legacyPeople, updatedAt: serverTimestamp() }, { merge: true });
      }
    } else {
      const incoming = profilesFromStoredValue(incomingValue).filter((item) => !removed.has(item.id));
      if (isDangerousProfilesOverwrite(incoming, existing)) throw new Error("Blocked an incomplete People save while protecting the organisation Directory.");
      const byId = new Map(existing.map((item) => [item.id, item]));
      incoming.forEach((item) => byId.set(item.id, item));
      next = [...byId.values()];
    }
    committed = { profiles: next, removed: [...removed] };
    transaction.set(ref, {
      localKey: "mc-profiles", value: JSON.stringify(next), json: next,
      removedProfileIds: committed.removed,
      updatedAt: serverTimestamp(), updatedByEmail: activeUser.email || "", updatedByUid: activeUser.uid || "",
      writeMode: "transactional-directory-v2",
    }, { merge: true });
  });
  if (activeOrgId !== orgId) return;
  removedProfileIds = new Set([...removedProfileIds, ...committed.removed]);
  recentLocalWrites.set("mc-profiles", Date.now());
  writeLocalFromCloud("mc-profiles", JSON.stringify(committed.profiles));
  if (legacyPeople) writeLocalFromCloud("mc-response-people", JSON.stringify(legacyPeople));
  try { window.dispatchEvent(new CustomEvent("mc-profile-change", { detail: committed.profiles })); } catch (_) {}
  recordMcTrafficFirestoreWrite("mc-profiles", JSON.stringify(committed.profiles));
  return committed.profiles;
}

export async function mcDeleteProfile(profileId = "") {
  const id = String(profileId || "").trim();
  if (!id) throw new Error("Choose a person to remove.");
  const requestedOrgId = activeOrgId;
  // Discard stale queued whole-list saves before the intentional transaction.
  scheduledWriteValues.delete("mc-profiles");
  pendingWrites.delete("mc-profiles");
  const run = () => {
    if (!requestedOrgId || activeOrgId !== requestedOrgId) throw new Error("The organisation changed. Open People and try again.");
    return mutateProfileAuthority(null, id);
  };
  profileWriteChain = profileWriteChain.catch(() => undefined).then(run);
  return profileWriteChain;
}

function sanitizeDocId(value = "") {
  return String(value || "")
    .replace(/^mc-/, "")
    .replace(/[^a-zA-Z0-9_-]/g, "_")
    .slice(0, 120) || "value";
}

function canonicalizeMcLocalKey(key = "") {
  const raw = String(key || "");
  if (!raw) return raw;
  // Older builds accidentally hydrated scoped docs as mc-logic-pass_inventory_X
  // / mc-import-active_inventory_X instead of the canonical colon scoped keys.
  if (/^mc-logic-pass_/i.test(raw)) return `mc-logic-pass:${raw.replace(/^mc-logic-pass_/i, "")}`;
  if (/^mc-import-active_/i.test(raw)) return `mc-import-active:${raw.replace(/^mc-import-active_/i, "")}`;
  if (/^mc-engine:/i.test(raw)) return raw.replace(/^mc-engine:/i, "mc-logic-pass:");
  return raw;
}

function docIdForLocalKey(key = "") {
  const raw = canonicalizeMcLocalKey(String(key || ""));
  if (KEY_TO_DOC[raw]) return KEY_TO_DOC[raw];
  if (raw === "mc-import-active") return "import_ALL";
  if (/^mc-import-active:/i.test(raw)) return `import_${sanitizeDocId(raw.replace(/^mc-import-active:/i, ""))}`;
  if (/^mc-logic-pass:/i.test(raw)) return `engine_${sanitizeDocId(raw.replace(/^mc-logic-pass:/i, ""))}`;
  if (/^mc-flag-config/i.test(raw)) return `config_${sanitizeDocId(raw.replace(/^mc-flag-config:?/i, ""))}`;
  if (/^mc-session-engine:/i.test(raw)) return `session-engine_${sanitizeDocId(raw.replace(/^mc-session-engine:/i, ""))}`;
  if (/^mc-count-ledger:/i.test(raw)) return `count-ledger_${sanitizeDocId(raw.replace(/^mc-count-ledger:/i, ""))}`;
  if (/^mc-session-detail:/i.test(raw)) return `sessionDetail_${sanitizeDocId(raw.replace(/^mc-session-detail:/i, ""))}`;
  if (/^mc-session-seed:/i.test(raw)) return `prepared_${sanitizeDocId(raw.replace(/^mc-session-seed:/i, ""))}`;
  if (/^mc-engine:/i.test(raw)) return `engine_${sanitizeDocId(raw.replace(/^mc-engine:/i, ""))}`;
  return sanitizeDocId(raw);
}

function localKeyForDoc(docId = "", data = {}) {
  const storedLocalKey = canonicalizeMcLocalKey(String(data.localKey || ""));

  // Canonical shared-list keys:
  // Older builds had duplicate mappings where the Firestore `archives` document
  // could hydrate as `mc-archive-records` instead of the key the Archive page
  // actually reads (`mc-session-archive`). That made finalized/restored rows
  // appear or disappear only after a later refresh/reconciliation. Always map
  // list docs back to the canonical UI keys so active/archived session changes
  // render live across devices.
  if (docId === "sessions" || storedLocalKey === "mc-session-records") return "mc-session-list";
  if (docId === "archives" || storedLocalKey === "mc-archive-records") return "mc-session-archive";

  if (storedLocalKey) return storedLocalKey;
  if (docId === "mc-profiles" || docId === "profiles") return "mc-profiles";
  const match = Object.entries(KEY_TO_DOC).find(([, value]) => value === docId);
  if (match) return match[0];
  if (docId === "import_ALL") return "mc-import-active";
  if (docId.startsWith("import_")) return `mc-import-active:${docId.replace(/^import_/, "")}`;
  if (docId === "logic-pass" || docId === "engine_ALL") return "mc-logic-pass";
  if (docId.startsWith("logic-pass_")) return `mc-logic-pass:${docId.replace(/^logic-pass_/, "")}`;
  if (docId.startsWith("import-active_")) return `mc-import-active:${docId.replace(/^import-active_/, "")}`;
  if (docId.startsWith("config_")) return `mc-flag-config:${docId.replace(/^config_/, "")}`;
  if (docId.startsWith("session-engine_")) return `mc-session-engine:${docId.replace(/^session-engine_/, "")}`;
  if (docId.startsWith("sessionDetail_")) return `mc-session-detail:${docId.replace(/^sessionDetail_/, "")}`;
  if (docId.startsWith("prepared_")) return `mc-session-seed:${docId.replace(/^prepared_/, "")}`;
  // Durable session authorities written by newer/older builds may survive without
  // an explicit data.localKey field. Recover the canonical local key from the
  // Firestore document id so historical live sessions remain discoverable.
  if (docId.startsWith("count-ledger_")) return `mc-count-ledger:${docId.replace(/^count-ledger_/, "")}`;
  if (docId.startsWith("session-completed_")) return `mc-session-completed:${docId.replace(/^session-completed_/, "")}`;
  if (docId.startsWith("session-checkpoint_")) return `mc-session-checkpoint:${docId.replace(/^session-checkpoint_/, "")}`;
  if (docId.startsWith("final-count-snapshot_")) return `mc-final-count-snapshot:${docId.replace(/^final-count-snapshot_/, "")}`;
  if (docId.startsWith("archive-session_")) return `mc-archive-session:${docId.replace(/^archive-session_/, "")}`;
  if (docId.startsWith("engine_")) return `mc-logic-pass:${docId.replace(/^engine_/, "")}`;
  return canonicalizeMcLocalKey(`mc-${docId}`);
}

function morpheusDocRef(orgId, docId) {
  return doc(db, "orgs", orgId, "morpheus", docId);
}

function extractOrgIdFromClaims(claims = {}) {
  const candidates = [
    claims?.orgId,
    claims?.organizationId,
    claims?.organisationId,
    claims?.orgID,
    claims?.org_id,
    claims?.activeOrgId,
    claims?.currentOrgId,
    claims?.tenantOrgId,
    claims?.tenantId,
    claims?.tenant,
    claims?.organization?.id,
    claims?.organisation?.id,
    claims?.org?.id,
    claims?.member?.orgId,
  ];
  const direct = candidates.find(Boolean);
  if (direct) return String(direct).trim();
  return "";
}

async function findOrgIdFromMember({ uid = "", email = "" } = {}) {
  const cleanUid = String(uid || "").trim();
  const cleanEmail = String(email || "").trim().toLowerCase();
  const attempts = [];
  if (cleanUid) attempts.push(query(collectionGroup(db, "members"), where("authUid", "==", cleanUid), limit(1)));
  if (cleanUid) attempts.push(query(collectionGroup(db, "members"), where("uid", "==", cleanUid), limit(1)));
  if (cleanEmail) attempts.push(query(collectionGroup(db, "members"), where("email", "==", cleanEmail), limit(1)));
  for (const q of attempts) {
    try {
      const snap = await getDocs(q);
      if (!snap.empty) return snap.docs[0].ref.parent.parent?.id || "";
    } catch (err) {
      console.warn("Morpheus Count could not resolve org from members query", err);
    }
  }
  return "";
}

function findOrgIdFromLocalStorage() {
  if (typeof window === "undefined") return "";
  const candidates = [];
  const push = (value) => {
    const raw = String(value || "").trim();
    if (!raw || raw.includes("@") || raw.includes("/") || raw.includes(" ")) return;
    if (raw.length < 10 || raw.length > 90) return;
    candidates.push(raw);
  };
  function walk(value, keyHint = "") {
    if (value == null) return;
    const key = String(keyHint || "").toLowerCase();
    if (typeof value === "string" || typeof value === "number") {
      if (key.includes("org") || key.includes("tenant") || key.includes("company")) push(value);
      return;
    }
    if (Array.isArray(value)) return value.forEach((item) => walk(item, keyHint));
    if (typeof value === "object") Object.entries(value).forEach(([k, v]) => walk(v, k));
  }
  [window.localStorage, window.sessionStorage].filter(Boolean).forEach((store) => {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i) || "";
      const raw = store.getItem(key);
      if (key.toLowerCase().includes("org")) push(raw);
      try { walk(JSON.parse(raw), key); } catch (_) {}
    }
  });
  return candidates[0] || "";
}

async function findOrgIdFromMorpheusOrg({ email = "", uid = "" } = {}) {
  const cleanEmail = String(email || "").trim().toLowerCase();
  const cleanUid = String(uid || "").trim();
  try {
    const orgSnap = await getDocs(query(collection(db, "orgs"), where("apps.morpheus", "==", true), limit(20)));
    for (const orgDoc of orgSnap.docs) {
      const checks = [];
      if (cleanUid) checks.push(query(collection(db, "orgs", orgDoc.id, "members"), where("authUid", "==", cleanUid), limit(1)));
      if (cleanUid) checks.push(query(collection(db, "orgs", orgDoc.id, "members"), where("uid", "==", cleanUid), limit(1)));
      if (cleanEmail) checks.push(query(collection(db, "orgs", orgDoc.id, "members"), where("email", "==", cleanEmail), limit(1)));
      for (const q of checks) {
        try {
          const snap = await getDocs(q);
          if (!snap.empty) return orgDoc.id;
        } catch (_) {}
      }
    }
  } catch (err) {
    console.warn("Morpheus Count could not resolve org from org/member fallback", err);
  }
  return "";
}

export async function resolveMcOrgId({ claims = {}, uid = "", email = "" } = {}) {
  const fromClaims = extractOrgIdFromClaims(claims);
  if (fromClaims) return fromClaims;

  // Never prefer browser/local org IDs over Firestore membership. Local storage was
  // the source of cross-org MC leakage when a browser moved from an old org to a
  // newly-created org. Resolve by UID/email first, then use local storage only as
  // a last-resort fallback for older sessions.
  if (!resolvingOrgPromise) {
    resolvingOrgPromise = findOrgIdFromMember({ uid, email }).finally(() => { resolvingOrgPromise = null; });
  }
  const fromMember = await resolvingOrgPromise;
  if (fromMember) return fromMember;
  const fromMorpheusOrg = await findOrgIdFromMorpheusOrg({ uid, email });
  if (fromMorpheusOrg) return fromMorpheusOrg;

  // Do not trust a browser-cached org for a signed-in identity. A demo/new org
  // can share the same browser as an old org, and local fallback was the source
  // of cross-org MC data leakage.
  if (String(uid || "").trim() || String(email || "").trim()) return "";
  return findOrgIdFromLocalStorage();
}

function dispatchStorageLikeEvent() {
  if (typeof window === "undefined") return;
  try { window.dispatchEvent(new Event("storage")); } catch (_) { window.dispatchEvent(new CustomEvent("storage")); }
  window.dispatchEvent(new CustomEvent(MC_SYNC_EVENT));
}

function dispatchReady(orgId, ready, extra = {}) {
  if (typeof window === "undefined") return;
  window.__morpheusSyncState = {
    orgId,
    ready,
    path: orgId ? `orgs/${orgId}/morpheus` : "",
    updatedAt: new Date().toISOString(),
    ...extra,
  };
  window.dispatchEvent(new CustomEvent(MC_SYNC_READY_EVENT, { detail: window.__morpheusSyncState }));
}

function isLargeSessionDetailKey(key = '') {
  return /^mc-session-engine:/i.test(String(key || ''));
}

function isPrioritySharedPayloadKey(key = '') {
  const clean = String(key || '');
  return clean === 'mc-sites'
    || clean === 'mc-site-responsibility-backup'
    || clean === 'mc-org-meta';
}

function isLargeSharedPayloadKey(key = '') {
  const clean = String(key || '');
  return isLargeSessionDetailKey(clean)
    || clean === 'mc-morpheus-order-sheets'
    || /^mc-import-active(?::|$)/i.test(clean)
    || /^mc-logic-pass(?::|$)/i.test(clean)
    || /^mc-engine:/i.test(clean);
}

function clearOldLargeSessionDetails() {
  if (typeof window === "undefined") return;
  try {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i) || "";
      if (/^mc-session-engine[:\-]/i.test(key)) keys.push(key);
    }
    keys.forEach((key) => {
      try {
        const raw = window.localStorage.getItem(key) || "";
        if (raw.length > 250000) {
          if (originalRemoveItem) originalRemoveItem.call(window.localStorage, key);
          else window.localStorage.removeItem(key);
        }
      } catch (_) {}
    });
  } catch (_) {}
}

function clearMcStoragePressure() {
  if (typeof window === "undefined") return;
  try {
    if (originalRemoveItem) originalRemoveItem.call(window.localStorage, "mc-activity-log");
    else window.localStorage.removeItem("mc-activity-log");
    window.sessionStorage?.removeItem("mc-activity-log");
  } catch (_) {}
  clearOldLargeSessionDetails();
  const removable = [];
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i) || "";
      if (/^mc-session-engine[:\-]/i.test(key)
        || /^mc-archive-session[:\-]/i.test(key)
        || /^mc-live-update[:\-]/i.test(key)
        || /^mc-link-inventory-snapshot(?::|$)/i.test(key)
        || /^mc-import-active(?::|$)/i.test(key)
        || /^mc-active-import-lock(?::|$)/i.test(key)
        || /^mc-latest-import-lock(?::|$)/i.test(key)
        || /^mc-engine:/i.test(key)) {
        const raw = window.localStorage.getItem(key) || "";
        removable.push({ key, size: raw.length });
      }
    }
  } catch (_) {}
  removable
    .sort((a, b) => b.size - a.size)
    .slice(0, 20)
    .forEach(({ key }) => {
      try {
        if (originalRemoveItem) originalRemoveItem.call(window.localStorage, key);
        else window.localStorage.removeItem(key);
      } catch (_) {}
    });
}

function writeSessionFallback(key, value) {
  try {
    window.sessionStorage?.setItem(String(key), String(value));
    return true;
  } catch (_) {
    return false;
  }
}

function purgeLegacyCategoryCache() {
  if (typeof window === "undefined") return;
  const keys = [];
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i) || "";
      if (key !== "mc-logic-pass" && !/^mc-engine:/i.test(key)) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = safeJsonParse(raw, null);
      // Only remove the old seeded/demo category payloads. Do not wipe a clean
      // org's newly-created category rules/groups on every refresh.
      if (isLegacySeededEnginePayload(key, { json: parsed, value: raw })) keys.push(key);
    }
  } catch (_) {}

  applyingCloud = true;
  try {
    keys.forEach((key) => {
      try {
        if (originalRemoveItem) originalRemoveItem.call(window.localStorage, key);
        else window.localStorage.removeItem(key);
      } catch (_) {}
    });
  } finally {
    applyingCloud = false;
  }
}


function clearHydratedMcCloudCache(keepKeys = new Set()) {
  if (typeof window === "undefined") return;
  const keys = new Set();
  [window.localStorage, window.sessionStorage].filter(Boolean).forEach((store) => {
    try {
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i) || "";
        if (!isMcKey(key) || isLocalOnlyMcKey(key) || keepKeys.has(key) || isCriticalSessionKey(key)) continue;
        keys.add(key);
      }
    } catch (_) {}
  });

  applyingCloud = true;
  try {
    Array.from(keys).forEach((key) => {
      try {
        if (originalRemoveItem) originalRemoveItem.call(window.localStorage, key);
        else window.localStorage.removeItem(key);
      } catch (_) {}
      try {
        if (!isLocalOnlyMcKey(key)) window.sessionStorage?.removeItem(key);
      } catch (_) {}
    });
  } finally {
    applyingCloud = false;
  }
}

function hasRecentLocalWrite(localKey = "", incomingValue = "") {
  const key = String(localKey || "");
  if (!key) return false;
  const writtenAt = recentLocalWrites.get(key);
  if (!writtenAt) return false;
  if (Date.now() - writtenAt > RECENT_LOCAL_WRITE_MS) {
    recentLocalWrites.delete(key);
    return false;
  }
  try {
    const current = window.localStorage?.getItem(key);
    // If the snapshot already matches the local write, let it apply and clear the guard.
    if (String(current || "") === String(incomingValue || "")) {
      recentLocalWrites.delete(key);
      return false;
    }
  } catch (_) {}
  return true;
}


function sessionPayloadTimestampFromValue(value = "") {
  try {
    const payload = typeof value === "string" ? JSON.parse(value || "null") : value;
    if (!payload || typeof payload !== "object") return 0;
    const candidates = [payload.savedAt, payload.meta?.lastEditedAt, payload.meta?.updatedAt, payload.meta?.startedAt].filter(Boolean);
    for (const candidate of candidates) {
      const time = Date.parse(candidate);
      if (Number.isFinite(time)) return time;
    }
  } catch (_) {}
  return 0;
}

function sessionPayloadTouchedCountFromValue(value = "") {
  try {
    const payload = typeof value === "string" ? JSON.parse(value || "null") : value;
    const lines = Array.isArray(payload?.lines) ? payload.lines : [];
    return lines.filter((line) => String(line?.counted ?? "").trim() || String(line?.waste ?? "").trim() || String(line?.note ?? "").trim()).length;
  } catch (_) {
    return 0;
  }
}

function isStaleSessionEngineCloudValue(localKey = "", incomingValue = "") {
  const key = String(localKey || "");
  if (!/^mc-session-(?:engine|checkpoint):/i.test(key) || typeof window === "undefined") return false;

  let currentValue = "";
  try { currentValue = window.localStorage?.getItem(key) || ""; } catch (_) {}
  if (!currentValue) {
    try { currentValue = window.sessionStorage?.getItem(key) || ""; } catch (_) {}
  }
  if (!currentValue) return false;

  const incomingTime = sessionPayloadTimestampFromValue(incomingValue);
  const currentTime = sessionPayloadTimestampFromValue(currentValue);
  const incomingTouched = sessionPayloadTouchedCountFromValue(incomingValue);
  const currentTouched = sessionPayloadTouchedCountFromValue(currentValue);

  // Session detail docs are large, frequently edited and shared between devices.
  // Firestore can briefly replay an older whole-session snapshot after a local
  // keystroke. Never let that older snapshot clear already-counted rows locally.
  if (currentTime && incomingTime && incomingTime < currentTime) return true;
  if (currentTime && !incomingTime && currentTouched > incomingTouched) return true;
  if (currentTime === incomingTime && currentTouched > incomingTouched) return true;
  return false;
}



function normalizeImportScopeFromLocalKey(localKey = "") {
  const key = String(localKey || "");
  const suffix = key.includes(":") ? key.split(":").slice(1).join(":") : "ALL";
  if (!suffix || suffix.toUpperCase() === "ALL") return "ALL";
  return suffix;
}

function readImportLockForLocalKey(localKey = "") {
  if (typeof window === "undefined") return null;
  const scope = normalizeImportScopeFromLocalKey(localKey);
  const candidates = importScopeLockCandidates(scope).flatMap((candidate) => [
    `mc-latest-import-lock:${candidate}`,
    `mc-active-import-lock:${candidate}`,
  ]);
  for (const key of candidates) {
    try {
      const raw = window.localStorage?.getItem(key);
      if (raw) return safeJsonParse(raw, null);
    } catch (_) {}
  }
  return null;
}

function importLockTime(lock = null) {
  try {
    const engine = lock?.engineSnapshot || lock?.engine || {};
    const candidates = [lock?.uploadedAt, lock?.createdAt, engine?.uploadedAt, engine?.lastUploadedAt, engine?.lastGeneratedAt]
      .filter(Boolean)
      .map((item) => Date.parse(item))
      .filter((time) => Number.isFinite(time));
    return Math.max(...candidates, 0);
  } catch (_) {
    return 0;
  }
}

function importLockFingerprint(lock = null) {
  const engine = lock?.engineSnapshot || lock?.engine || {};
  return String(lock?.fingerprint || lock?.importFingerprint || lock?.importId || engine?.importFingerprint || engine?.activeImportId || engine?.importSourceId || "");
}

function importLockRowCount(lock = null) {
  try {
    const engine = lock?.engineSnapshot || lock?.engine || {};
    return Math.max(
      Number(lock?.rowCount || 0),
      Array.isArray(engine?.rawRows) ? engine.rawRows.length : 0,
      Array.isArray(engine?.preparedRows) ? engine.preparedRows.length : 0
    );
  } catch (_) {
    return 0;
  }
}

function incomingImportFingerprint(value = "") {
  try {
    const payload = safeJsonParse(value, null);
    const engine = payload?.engine || payload || {};
    return String(payload?.importFingerprint || payload?.activeImportId || engine?.importFingerprint || engine?.activeImportId || engine?.importSourceId || "");
  } catch (_) {
    return "";
  }
}

function enginePayloadTimestampFromValue(value = "") {
  try {
    const payload = typeof value === "string" ? JSON.parse(value || "null") : value;
    if (!payload || typeof payload !== "object") return 0;
    const history = Array.isArray(payload.importHistory) ? payload.importHistory : [];
    const uploads = Array.isArray(payload.uploadHistory) ? payload.uploadHistory : [];
    const latestHistory = [...history, ...uploads]
      .map((item) => item?.uploadedAt || item?.createdAt || item?.updatedAt)
      .filter(Boolean)
      .map((item) => Date.parse(item))
      .filter((time) => Number.isFinite(time))
      .sort((a, b) => b - a)[0] || 0;
    const publicationTimes = Object.values(payload.config?.configurationSectionVersions || {})
      .map((item) => Date.parse(item?.publishedAt || item?.updatedAt || ""))
      .filter((time) => Number.isFinite(time));
    const candidates = [
      payload.uploadedAt,
      payload.lastUploadedAt,
      payload.lastGeneratedAt,
      payload.updatedAt,
      payload.syncedAt,
      payload.config?.lastCsvUploadedAt,
      ...(publicationTimes.length ? [new Date(Math.max(...publicationTimes)).toISOString()] : []),
    ]
      .filter(Boolean)
      .map((item) => Date.parse(item))
      .filter((time) => Number.isFinite(time));
    return Math.max(latestHistory, ...candidates, 0);
  } catch (_) {
    return 0;
  }
}

function enginePayloadRowCountFromValue(value = "") {
  try {
    const payload = typeof value === "string" ? JSON.parse(value || "null") : value;
    if (!payload || typeof payload !== "object") return 0;
    return Math.max(
      Array.isArray(payload.rawRows) ? payload.rawRows.length : 0,
      Array.isArray(payload.preparedRows) ? payload.preparedRows.length : 0
    );
  } catch (_) {
    return 0;
  }
}

function isStaleEngineCloudValue(localKey = "", incomingValue = "") {
  const key = String(localKey || "");
  if (!(/^mc-logic-pass(?::|$)/i.test(key) || /^mc-engine:/i.test(key)) || typeof window === "undefined") return false;

  const lock = readImportLockForLocalKey(key);
  const lockTime = importLockTime(lock);
  const incomingTimeForLock = enginePayloadTimestampFromValue(incomingValue);
  const lockFingerprint = importLockFingerprint(lock);
  const lockRows = importLockRowCount(lock);
  const incomingRowsForLock = enginePayloadRowCountFromValue(incomingValue);
  const incomingFingerprintForLock = incomingImportFingerprint(incomingValue);
  if (lockRows && incomingRowsForLock && incomingRowsForLock < lockRows && (!incomingTimeForLock || incomingTimeForLock <= lockTime || !incomingFingerprintForLock || incomingFingerprintForLock !== lockFingerprint)) return true;
  if (lockTime && (!incomingTimeForLock || incomingTimeForLock < lockTime)) return true;
  if (lockTime && lockFingerprint && incomingFingerprintForLock && incomingFingerprintForLock !== lockFingerprint && incomingTimeForLock <= lockTime) return true;
  if (lockTime && lockFingerprint && !incomingFingerprintForLock && incomingTimeForLock <= lockTime) return true;

  let currentValue = "";
  try { currentValue = window.localStorage?.getItem(key) || ""; } catch (_) {}
  if (!currentValue) {
    try { currentValue = window.sessionStorage?.getItem(key) || ""; } catch (_) {}
  }
  if (!currentValue) return false;

  const incomingTime = enginePayloadTimestampFromValue(incomingValue);
  const currentTime = enginePayloadTimestampFromValue(currentValue);
  if (currentTime && incomingTime && incomingTime < currentTime) return true;

  const incomingRows = enginePayloadRowCountFromValue(incomingValue);
  const currentRows = enginePayloadRowCountFromValue(currentValue);

  // HARD PROTECTION:
  // Never allow a smaller incoming engine/import payload to overwrite a larger
  // already-hydrated local payload for the same browser session.
  // This specifically prevents the District Six 1-row stale snapshot from
  // replacing the fresh 93-row import after refresh/navigation.
  if (currentRows > 0 && incomingRows > 0 && incomingRows < currentRows) {
    return true;
  }

  // Do not let an older/empty engine snapshot replace a freshly uploaded CSV for the same site.
  if (currentTime && !incomingTime && currentRows > incomingRows) return true;

  try {
    const incoming = safeJsonParse(incomingValue, null) || {};
    const current = safeJsonParse(currentValue, null) || {};
    const currentFingerprint = current?.importFingerprint || current?.activeImportId || current?.importSourceId || "";
    const incomingFingerprint = incoming?.importFingerprint || incoming?.activeImportId || incoming?.importSourceId || "";

    // Previous patch versions could write a newer uploadedAt/fileName onto an older row set.
    // If this browser has a real freshly uploaded CSV fingerprint, do not accept an incoming
    // engine for the same/newer timestamp that cannot prove it is the same import.
    if (currentFingerprint && !incomingFingerprint && currentTime >= incomingTime && currentRows >= incomingRows) return true;
    if (currentFingerprint && incomingFingerprint && currentFingerprint !== incomingFingerprint && currentTime >= incomingTime) return true;
  } catch (_) {}

  return false;
}


function activeImportTimeFromValue(value = "") {
  try {
    const payload = safeJsonParse(value, null);
    const engine = payload?.engine || payload || {};
    const candidates = [
      payload?.uploadedAt,
      engine?.uploadedAt,
      engine?.lastUploadedAt,
      engine?.lastGeneratedAt,
      engine?.updatedAt,
    ].filter(Boolean).map((item) => Date.parse(item)).filter((time) => Number.isFinite(time));
    return Math.max(...candidates, 0);
  } catch (_) {
    return 0;
  }
}

function activeImportFingerprintFromValue(value = "") {
  try {
    const payload = safeJsonParse(value, null);
    const engine = payload?.engine || payload || {};
    return String(payload?.importFingerprint || payload?.activeImportId || engine?.importFingerprint || engine?.activeImportId || engine?.importSourceId || "");
  } catch (_) {
    return "";
  }
}

function activeImportRowCountFromValue(value = "") {
  try {
    const payload = safeJsonParse(value, null);
    const engine = payload?.engine || payload || {};
    return Math.max(
      Number(payload?.rowCount || 0),
      Array.isArray(engine?.rawRows) ? engine.rawRows.length : 0,
      Array.isArray(engine?.preparedRows) ? engine.preparedRows.length : 0
    );
  } catch (_) {
    return 0;
  }
}

function isStaleActiveImportCloudValue(localKey = "", incomingValue = "") {
  if (!/^mc-import-active(?::|$)/i.test(String(localKey || "")) || typeof window === "undefined") return false;
  const lock = readImportLockForLocalKey(localKey);
  const lockTime = importLockTime(lock);
  const incomingTimeForLock = activeImportTimeFromValue(incomingValue);
  const lockFingerprint = importLockFingerprint(lock);
  const lockRows = importLockRowCount(lock);
  const incomingRowsForLock = activeImportRowCountFromValue(incomingValue);
  const incomingFingerprintForLock = activeImportFingerprintFromValue(incomingValue);
  if (lockRows && incomingRowsForLock && incomingRowsForLock < lockRows && (!incomingTimeForLock || incomingTimeForLock <= lockTime || !incomingFingerprintForLock || incomingFingerprintForLock !== lockFingerprint)) return true;
  if (lockTime && (!incomingTimeForLock || incomingTimeForLock < lockTime)) return true;
  if (lockTime && lockFingerprint && incomingFingerprintForLock && incomingFingerprintForLock !== lockFingerprint && incomingTimeForLock <= lockTime) return true;
  if (lockTime && lockFingerprint && !incomingFingerprintForLock && incomingTimeForLock <= lockTime) return true;

  let currentValue = "";
  try { currentValue = window.localStorage?.getItem(localKey) || ""; } catch (_) {}
  if (!currentValue) return false;

  const currentTime = activeImportTimeFromValue(currentValue);
  const incomingTime = activeImportTimeFromValue(incomingValue);
  if (currentTime && incomingTime && incomingTime < currentTime) return true;

  const currentFingerprint = activeImportFingerprintFromValue(currentValue);
  const incomingFingerprint = activeImportFingerprintFromValue(incomingValue);
  if (currentFingerprint && incomingFingerprint && currentFingerprint !== incomingFingerprint && currentTime >= incomingTime) return true;
  if (currentFingerprint && !incomingFingerprint && currentTime >= incomingTime) return true;

  return false;
}

function sessionListTimeFromValue(value = "") {
  try {
    const rows = safeJsonParse(value, []);
    if (!Array.isArray(rows)) return 0;
    return rows
      .map((item) => Date.parse(item?.updatedAt || item?.startedAt || item?.createdAt || ""))
      .filter((time) => Number.isFinite(time))
      .sort((a, b) => b - a)[0] || 0;
  } catch (_) {
    return 0;
  }
}

function sessionIdsFromValue(value = "") {
  try {
    const rows = safeJsonParse(value, []);
    if (!Array.isArray(rows)) return new Set();
    return new Set(rows.map((item) => String(item?.sessionId || "").trim()).filter(Boolean));
  } catch (_) {
    return new Set();
  }
}


function sessionRecordTime(record = {}) {
  const candidates = [record?.updatedAt, record?.startedAt, record?.createdAt, record?.lastEditedAt, record?.archivedAt]
    .filter(Boolean)
    .map((item) => Date.parse(item))
    .filter((time) => Number.isFinite(time));
  return Math.max(...candidates, 0);
}

function activeSessionArrayFromValue(value = "") {
  const rows = safeJsonParse(value, []);
  if (!Array.isArray(rows)) return [];
  return rows.filter((item) => item && item.sessionId);
}

function pendingLocalSessionArray() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage?.getItem("__mc_session_local_pending") || "";
    const parsed = safeJsonParse(raw, null);
    const updatedAt = Date.parse(parsed?.updatedAt || "");
    if (!Number.isFinite(updatedAt) || Date.now() - updatedAt > 24 * 60 * 60 * 1000) return [];
    return activeSessionArrayFromValue(JSON.stringify(parsed?.records || []));
  } catch (_) {
    return [];
  }
}

function localRestoreOverrideIds() {
  const ids = new Set();
  if (typeof window === "undefined") return ids;
  try {
    const raw = window.localStorage?.getItem("__mc_session_restore_overrides") || "{}";
    const parsed = safeJsonParse(raw, {});
    Object.entries(parsed || {}).forEach(([id, time]) => {
      const stamp = Date.parse(time || "");
      if (id && Number.isFinite(stamp) && Date.now() - stamp < 24 * 60 * 60 * 1000) ids.add(String(id).trim());
    });
  } catch (_) {}
  return ids;
}

function localArchiveOverrideIds() {
  const ids = new Set();
  if (typeof window === "undefined") return ids;
  try {
    const raw = window.localStorage?.getItem("__mc_session_archive_overrides") || "{}";
    const parsed = safeJsonParse(raw, {});
    Object.entries(parsed || {}).forEach(([id, time]) => {
      const stamp = Date.parse(time || "");
      if (id && Number.isFinite(stamp) && Date.now() - stamp < 24 * 60 * 60 * 1000) ids.add(String(id).trim());
    });
  } catch (_) {}
  return ids;
}

function filterArchiveValueForLocalRestores(value = "") {
  const restored = localRestoreOverrideIds();
  if (!restored.size) return value;
  const rows = safeJsonParse(value, []);
  if (!Array.isArray(rows)) return value;
  return JSON.stringify(rows.filter((item) => !restored.has(String(item?.sessionId || "").trim())));
}

function archivedSessionIdsFromLocalAndCloudValue(extraArchiveValue = "") {
  const ids = new Set();
  const addRows = (rows) => {
    (Array.isArray(rows) ? rows : []).forEach((item) => {
      const sessionId = String(item?.sessionId || "").trim();
      if (sessionId) ids.add(sessionId);
    });
  };
  try { addRows(safeJsonParse((window.localStorage?.getItem("mc-session-archive") || window.localStorage?.getItem("mc-archive-records")) || "", [])); } catch (_) {}
  try { addRows(safeJsonParse((window.sessionStorage?.getItem("mc-session-archive") || window.sessionStorage?.getItem("mc-archive-records")) || "", [])); } catch (_) {}
  const scanArchiveKeys = (area) => {
    if (!area) return;
    try {
      for (let i = 0; i < area.length; i += 1) {
        const key = area.key(i);
        if (!key || !key.startsWith("mc-archive-session:")) continue;
        const idFromKey = key.replace("mc-archive-session:", "").trim();
        try {
          const parsed = safeJsonParse(area.getItem(key) || "{}", {});
          const id = String(parsed?.sessionId || idFromKey || "").trim();
          if (id) ids.add(id);
        } catch (_) {
          if (idFromKey) ids.add(idFromKey);
        }
      }
    } catch (_) {}
  };
  try { scanArchiveKeys(window.localStorage); } catch (_) {}
  try { scanArchiveKeys(window.sessionStorage); } catch (_) {}
  if (extraArchiveValue) addRows(safeJsonParse(extraArchiveValue, []));
  localRestoreOverrideIds().forEach((id) => ids.delete(id));
  localArchiveOverrideIds().forEach((id) => ids.add(id));
  return ids;
}


function hasSessionCycleMetadata(record = {}) {
  return Boolean(
    record?.cycleId || record?.cycleKey || record?.cycleName || record?.cycle?.cycleId || record?.cycle?.id || record?.cycle?.name || record?.meta?.cycleId || record?.meta?.cycleName
  );
}

function hasSessionSectionMetadata(record = {}) {
  return Boolean(
    record?.sectionId || record?.sectionName || record?.fullSite === true || record?.isFullSite === true || record?.fullCount === true || record?.meta?.sectionId || record?.meta?.sectionName || record?.meta?.fullSite === true
  );
}

function mergeSessionRecordPreservingMetadata(existing = {}, incoming = {}) {
  const existingTime = sessionRecordTime(existing);
  const incomingTime = sessionRecordTime(incoming);
  const base = incomingTime >= existingTime ? { ...existing, ...incoming } : { ...incoming, ...existing };
  const metadataRich = {
    cycleId: hasSessionCycleMetadata(incoming) ? (incoming.cycleId || incoming.cycleKey || incoming.meta?.cycleId || '') : (existing.cycleId || existing.cycleKey || existing.meta?.cycleId || ''),
    cycleName: hasSessionCycleMetadata(incoming) ? (incoming.cycleName || incoming.cycle?.name || incoming.meta?.cycleName || '') : (existing.cycleName || existing.cycle?.name || existing.meta?.cycleName || ''),
    cycleKey: hasSessionCycleMetadata(incoming) ? (incoming.cycleKey || incoming.cycleId || incoming.meta?.cycleKey || '') : (existing.cycleKey || existing.cycleId || existing.meta?.cycleKey || ''),
    cycle: hasSessionCycleMetadata(incoming) ? (incoming.cycle || incoming.meta?.cycle) : (existing.cycle || existing.meta?.cycle),
    sectionId: hasSessionSectionMetadata(incoming) ? (incoming.sectionId || incoming.meta?.sectionId || '') : (existing.sectionId || existing.meta?.sectionId || ''),
    sectionName: hasSessionSectionMetadata(incoming) ? (incoming.sectionName || incoming.meta?.sectionName || '') : (existing.sectionName || existing.meta?.sectionName || ''),
    fullSite: incoming.fullSite === true || incoming.isFullSite === true || incoming.fullCount === true || existing.fullSite === true || existing.isFullSite === true || existing.fullCount === true,
    sessionType: incoming.sessionType || incoming.meta?.sessionType || existing.sessionType || existing.meta?.sessionType || '',
    countScope: incoming.countScope || incoming.meta?.countScope || existing.countScope || existing.meta?.countScope || '',
  };
  return {
    ...base,
    ...metadataRich,
    isFullSite: metadataRich.fullSite,
    fullCount: metadataRich.fullSite,
    meta: {
      ...(existing.meta || {}),
      ...(incoming.meta || {}),
      ...metadataRich,
    },
  };
}

function sessionRecordBelongsToActiveOrg(record = {}, orgId = activeOrgId) {
  const expected = String(orgId || "").trim();
  const stamped = String(record?.tenantOrgId || record?.orgId || record?.organisationId || "").trim();
  // Strict tenant boundary: legacy unstamped rows are not safe to display or merge.
  // They may have been written by an older build into the wrong organisation cache.
  return Boolean(expected && stamped && stamped === expected);
}

function stampSessionRowsForActiveOrg(rows = [], orgId = activeOrgId) {
  const expected = String(orgId || "").trim();
  return (Array.isArray(rows) ? rows : [])
    .filter(Boolean)
    .map((record) => ({ ...record, tenantOrgId: expected, orgId: record?.orgId || expected }));
}

function mergeActiveSessionListValues(currentValue = "", incomingValue = "", extraArchiveValue = "") {
  const archivedIds = archivedSessionIdsFromLocalAndCloudValue(extraArchiveValue);
  const incomingRows = activeSessionArrayFromValue(incomingValue);
  const incomingTime = sessionListTimeFromValue(incomingValue);
  const now = Date.now();
  const byId = new Map();

  const add = (record) => {
    if (!sessionRecordBelongsToActiveOrg(record)) return;
    const sessionId = String(record?.sessionId || "").trim();
    if (!sessionId || archivedIds.has(sessionId)) return;
    if (record?.archived || record?.archivedAt || String(record?.status || "").toLowerCase() === "archived") return;
    const existing = byId.get(sessionId);
    if (!existing) byId.set(sessionId, record);
    else byId.set(sessionId, mergeSessionRecordPreservingMetadata(existing, record));
  };

  // Preserve active local rows while merging the cloud list. Firestore snapshots can
  // arrive out of order or briefly miss a live session after refresh/tab changes;
  // using cloud as the only authority made Live Sessions disappear. Archive/finalize
  // still wins through archivedIds and archived flags above.
  [...incomingRows, ...activeSessionArrayFromValue(currentValue), ...pendingLocalSessionArray()].forEach(add);

  return Array.from(byId.values()).sort((a, b) => sessionRecordTime(b) - sessionRecordTime(a));
}

function readSessionListLock() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage?.getItem("mc-session-list-lock");
    return raw ? safeJsonParse(raw, null) : null;
  } catch (_) {
    return null;
  }
}

function isStaleSessionListCloudValue(localKey = "", incomingValue = "") {
  if (String(localKey || "") !== "mc-session-list" || typeof window === "undefined") return false;
  let currentValue = "";
  try { currentValue = window.localStorage?.getItem("mc-session-list") || ""; } catch (_) {}
  const pendingRowsForStaleCheck = pendingLocalSessionArray();
  if (!currentValue && !pendingRowsForStaleCheck.length) return false;
  if (!currentValue && pendingRowsForStaleCheck.length) currentValue = JSON.stringify(pendingRowsForStaleCheck);

  const currentTime = sessionListTimeFromValue(currentValue);
  const incomingTime = sessionListTimeFromValue(incomingValue);
  if (currentTime && incomingTime && incomingTime < currentTime) return true;

  const currentIds = sessionIdsFromValue(currentValue);
  pendingLocalSessionArray().forEach((record) => {
    const id = String(record?.sessionId || "").trim();
    if (id) currentIds.add(id);
  });
  const incomingIds = sessionIdsFromValue(incomingValue);
  const lock = readSessionListLock();
  const lockIds = Array.isArray(lock?.ids) ? lock.ids.map((id) => String(id || "").trim()).filter(Boolean) : [];
  const lockTime = Date.parse(lock?.updatedAt || lock?.createdAt || "");
  if (lockIds.length && Number.isFinite(lockTime)) {
    const missingLockedIds = lockIds.filter((id) => !incomingIds.has(id));
    if (missingLockedIds.length && Date.now() - lockTime < 60 * 60 * 1000) return true;
  }
  const missingLocalIds = Array.from(currentIds).filter((id) => !incomingIds.has(id));
  // Protect just-created sessions from disappearing when a slower Firestore snapshot
  // replays an older session-list document immediately after session creation.
  if (missingLocalIds.length && currentTime && (!incomingTime || currentTime >= incomingTime)) {
    const recentWindowMs = 30 * 60 * 1000;
    if (Date.now() - currentTime < recentWindowMs) return true;
  }
  return false;
}

function cloudValueFromDocData(data = {}) {
  if (typeof data.value === "string") return data.value;
  if (data.json !== undefined) return JSON.stringify(data.json);
  return "";
}

function firestoreTimestampMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") {
    try { return value.toMillis(); } catch (_) {}
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function timestampForLocalKeyValue(localKey = "", value = "", data = {}) {
  const key = String(localKey || "");
  let time = 0;
  if (/^mc-import-active(?::|$)/i.test(key)) time = activeImportTimeFromValue(value);
  else if (/^mc-logic-pass(?::|$)/i.test(key) || /^mc-engine:/i.test(key)) time = enginePayloadTimestampFromValue(value);
  else if (key === "mc-session-list") time = sessionListTimeFromValue(value);
  else if (/^mc-session-engine:/i.test(key) || /^mc-session-checkpoint:/i.test(key)) time = sessionPayloadTimestampFromValue(value);
  const cloudUpdated = firestoreTimestampMillis(data?.updatedAt);
  return Math.max(time || 0, cloudUpdated || 0);
}

function applyMorpheusSnapshotToLocal(snapshot, { replaceCache = false } = {}) {
  if (!snapshot || typeof window === "undefined") return { appliedKeys: new Set(), hasProfilesDoc: false, hasOrgDataDocs: false };

  const appliedKeys = new Set();
  const docs = Array.isArray(snapshot.docs) ? snapshot.docs : [];
  const hasProfilesDoc = docs.some((item) => {
    const data = item?.data?.() || {};
    return item?.id === "profiles" || item?.id === "mc-profiles" || String(data.localKey || "") === "mc-profiles";
  });
  const hasOrgDataDocs = docs.some((item) => item.id !== "meta");

  docs.forEach((docSnap) => {
    if (!docSnap || docSnap.id === "meta") return;
    const data = docSnap.data?.() || {};
    if (isImportSnapshotStorageDoc(docSnap.id, data)) return;
    const localKey = localKeyForDoc(docSnap.id, data);
    if (!isMcKey(localKey) || isLocalOnlyMcKey(localKey)) return;
    if (activeCleanBootstrap && isLegacySeededEnginePayload(localKey, data)) {
      deleteDoc(docSnap.ref).catch((err) => console.warn("Morpheus Count could not delete legacy seeded engine doc", err));
      return;
    }
    appliedKeys.add(localKey);
  });

  if (replaceCache) {
    // Do not purge local values that the user changed while this first snapshot
    // was loading. They are queued in pendingWrites and will be pushed to
    // Firestore immediately after hydration completes.
    const protectedKeys = new Set([...appliedKeys, ...pendingWrites.keys()]);
    clearHydratedMcCloudCache(protectedKeys);
  }

  // A legacy Firestore document named `session-list` can still exist beside the
  // canonical `sessions` document. Both advertise localKey=mc-session-list, so
  // applying both lets the stale legacy [] overwrite/merge into the live ledger
  // depending on snapshot order. When canonical `sessions` exists it is the only
  // authority for mc-session-list; legacy list docs are fallback-only.
  const hasCanonicalSessionsDoc = docs.some((docSnap) => docSnap?.id === "sessions");
  const canonicalProfiles = docs.find((docSnap) => docSnap?.id === "profiles");
  const profileAuthority = canonicalProfiles || docs.find((docSnap) => docSnap?.id === "mc-profiles");
  if (profileAuthority) removedProfileIds = new Set([...removedProfileIds, ...(profileAuthority.data()?.removedProfileIds || [])]);

  const cloudItems = docs
    .map((docSnap, order) => {
      if (!docSnap || docSnap.id === "meta") return null;
      const data = docSnap.data?.() || {};
      if (isImportSnapshotStorageDoc(docSnap.id, data)) return null;
      const localKey = localKeyForDoc(docSnap.id, data);
      if (!isMcKey(localKey) || isLocalOnlyMcKey(localKey)) return null;
      if (localKey === "mc-session-list" && hasCanonicalSessionsDoc && docSnap.id !== "sessions") {
        return null;
      }
      if (localKey === "mc-profiles" && canonicalProfiles && docSnap.id !== "profiles") return null;
      const incomingValue = cloudValueFromDocData(data);
      return {
        docSnap,
        data,
        localKey,
        incomingValue,
        time: timestampForLocalKeyValue(localKey, incomingValue, data),
        order,
      };
    })
    .filter(Boolean)
    // If legacy and canonical Firestore docs both exist for the same local key,
    // always apply the older one first and the newest one last. This prevents the
    // 22nd CSV / ghost session cache from winning purely because Firestore returned
    // documents in an arbitrary order.
    .sort((a, b) => {
      if (a.localKey !== b.localKey) return a.order - b.order;
      if (a.time !== b.time) return a.time - b.time;
      return a.order - b.order;
    });

  cloudItems.forEach(({ data, localKey, incomingValue }) => {
    if (activeCleanBootstrap && isLegacySeededEnginePayload(localKey, data)) return;
    if (!incomingValue) return;
    if (localKey === "mc-session-archive") {
      const tenantRows = safeJsonParse(incomingValue, []).filter((row) => sessionRecordBelongsToActiveOrg(row));
      const filteredArchiveValue = filterArchiveValueForLocalRestores(JSON.stringify(tenantRows));
      if (hasRecentLocalWrite(localKey, filteredArchiveValue)) return;
      writeLocalFromCloud(localKey, filteredArchiveValue);
      return;
    }
    if (localKey === "mc-session-list") {
      // Active sessions are operational state. On refresh, a slower/empty cloud
      // snapshot must not wipe a session that was just prepared locally and is
      // still being written to Firestore. Merge only fresh local rows with the
      // incoming cloud list, while still filtering anything already archived.
      if (isStaleSessionListCloudValue(localKey, incomingValue)) return;
      if (hasRecentLocalWrite(localKey, incomingValue)) return;
      let currentValue = "[]";
      try { currentValue = window.localStorage?.getItem("mc-session-list") || window.sessionStorage?.getItem("mc-session-list") || "[]"; } catch (_) {}
      let archiveValue = "[]";
      try { archiveValue = window.localStorage?.getItem("mc-session-archive") || window.sessionStorage?.getItem("mc-session-archive") || "[]"; } catch (_) {}
      const strictIncomingRows = activeSessionArrayFromValue(incomingValue).filter((row) => sessionRecordBelongsToActiveOrg(row));
      const mergedRows = mergeActiveSessionListValues(currentValue, JSON.stringify(strictIncomingRows), archiveValue);
      writeLocalFromCloud(localKey, JSON.stringify(mergedRows));
      return;
    }
    if (isStaleSessionListCloudValue(localKey, incomingValue)) return;
    if (isStaleSessionEngineCloudValue(localKey, incomingValue)) return;
    if (isStaleActiveImportCloudValue(localKey, incomingValue)) return;
    if (isStaleEngineCloudValue(localKey, incomingValue)) return;
    if (hasRecentLocalWrite(localKey, incomingValue)) return;
    writeLocalFromCloud(localKey, incomingValue);
  });

  return { appliedKeys, hasProfilesDoc, hasOrgDataDocs };
}

function writeLocalFromCloud(key, value) {
  if (typeof window === "undefined" || !key || typeof value !== "string") return;
  const stringKey = String(key || "");
  volatileMcCache.set(stringKey, value);

  // Cloud hydration is a cache operation, not an operational write. Large
  // Morpheus payloads belong in IndexedDB instead of repeatedly filling the
  // browser's tiny synchronous localStorage bucket. The in-memory cache above
  // keeps the freshly hydrated value immediately available to the current page.
  if (shouldUseIndexedDb(stringKey, value)) {
    idbSetItem(stringKey, value, { reason: "cloud-hydration-cache" }).catch((error) => {
      console.warn("Morpheus IndexedDB cloud hydration cache write failed", { key: stringKey, error });
    });
    try {
      if (originalRemoveItem) originalRemoveItem.call(window.localStorage, stringKey);
      else window.localStorage.removeItem(stringKey);
    } catch (_) {}
    try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
    return;
  }

  applyingCloud = true;
  try {
    try {
      if (originalSetItem) originalSetItem.call(window.localStorage, stringKey, value);
      else window.localStorage.setItem(stringKey, value);
      try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
      return;
    } catch (err) {
      if (err?.name !== "QuotaExceededError") throw err;
    }

    // Make one real attempt to create headroom before falling back. This moves
    // eligible large MC caches to IndexedDB rather than deleting operational
    // cloud data, so local cache pressure cannot affect Firestore truth.
    clearMcStoragePressure();
    freeLocalStorageForCriticalWrite(Math.max(256 * 1024, value.length * 2));

    try {
      if (originalSetItem) originalSetItem.call(window.localStorage, stringKey, value);
      else window.localStorage.setItem(stringKey, value);
      try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
      return;
    } catch (retryErr) {
      if (retryErr?.name !== "QuotaExceededError") throw retryErr;
    }

    // A successful sessionStorage fallback is still a successful cache write.
    // Previously we logged a warning even when this fallback worked, producing
    // hundreds/thousands of scary console messages during normal hydration.
    if (writeSessionFallback(stringKey, value)) return;

    // Firestore + volatileMcCache remain authoritative/usable even if both web
    // storage buckets are completely full. One warning is enough for diagnosis.
    if (!writeLocalFromCloud.quotaWarnings) writeLocalFromCloud.quotaWarnings = new Set();
    if (!writeLocalFromCloud.quotaWarnings.has(stringKey)) {
      writeLocalFromCloud.quotaWarnings.add(stringKey);
      console.warn("Morpheus Count could not persist local cache because browser storage is full", { key: stringKey });
    }
  } finally {
    applyingCloud = false;
  }
}


function isSessionStatePayloadKey(key = "") {
  const clean = String(key || "");
  return /^mc-session-engine:/i.test(clean) || /^mc-session-checkpoint:/i.test(clean) || /^mc-count-ledger:/i.test(clean);
}

function isTerminalSessionPayload(payload = {}) {
  const status = String(payload?.meta?.status || "").trim().toLowerCase();
  return Boolean(payload?.meta?.sectionCompletedAt || payload?.meta?.completedAt || ["section complete", "completed", "complete"].includes(status));
}

function normalizeFieldVersion(version = null) {
  if (!version || typeof version !== "object") return { at: 0, writer: "", seq: 0 };
  return {
    at: Number(version.at || 0),
    writer: String(version.writer || ""),
    seq: Number(version.seq || 0),
  };
}

function compareFieldVersions(a = null, b = null) {
  const av = normalizeFieldVersion(a);
  const bv = normalizeFieldVersion(b);
  if (av.at !== bv.at) return av.at - bv.at;
  if (av.seq !== bv.seq) return av.seq - bv.seq;
  return av.writer.localeCompare(bv.writer);
}

function mergeSessionPayloadValues(existingValue = "", incomingValue = "") {
  const existing = safeJsonParse(existingValue, null);
  const incoming = safeJsonParse(incomingValue, null);
  if (!incoming || !Array.isArray(incoming?.lines)) return incomingValue;
  if (!existing || !Array.isArray(existing?.lines)) return incomingValue;

  // FOUNDATION RULES FOR LIVE COUNTS:
  // 1. Firestore is authoritative; browser storage is only a cache.
  // 2. A blank/stale whole-session payload may NEVER erase a previously persisted
  //    count/waste/note from another device.
  // 3. Terminal state is monotonic: once completed, an Active autosave can never
  //    reopen or partially replace the count.
  // 4. Rows are merged independently, so two devices counting different products
  //    cannot destroy each other's work through last-write-wins document writes.
  if (isTerminalSessionPayload(existing) && !isTerminalSessionPayload(incoming)) {
    return existingValue;
  }

  const existingById = new Map(existing.lines.map((line, index) => [String(line?.id ?? line?.sku ?? index), line]));
  const incomingById = new Map(incoming.lines.map((line, index) => [String(line?.id ?? line?.sku ?? index), line]));
  const orderedIds = [];
  existing.lines.forEach((line, index) => orderedIds.push(String(line?.id ?? line?.sku ?? index)));
  incoming.lines.forEach((line, index) => {
    const id = String(line?.id ?? line?.sku ?? index);
    if (!orderedIds.includes(id)) orderedIds.push(id);
  });

  const meaningful = (value) => value !== null && value !== undefined && String(value).trim() !== "";
  const mergedLines = orderedIds.map((id) => {
    const oldLine = existingById.get(id) || {};
    const newLine = incomingById.get(id) || {};
    const merged = { ...oldLine, ...newLine };
    const oldVersions = oldLine?._mcFieldVersions || {};
    const newVersions = newLine?._mcFieldVersions || {};
    const mergedVersions = { ...oldVersions, ...newVersions };
    ["counted", "waste", "note"].forEach((field) => {
      const oldValue = oldLine?.[field];
      const newValue = newLine?.[field];
      const oldVersion = oldVersions?.[field];
      const newVersion = newVersions?.[field];
      const versionOrder = compareFieldVersions(newVersion, oldVersion);

      // Canonical ledger rule: field versions win before arrival order. This closes
      // the "82 becomes 8" race where the first keystroke's slower transaction
      // finished after the second keystroke and previously overwrote it.
      if (oldVersion || newVersion) {
        if (!newVersion || versionOrder < 0) {
          merged[field] = oldValue;
          mergedVersions[field] = oldVersion;
          return;
        }
        if (versionOrder >= 0) {
          // A versioned field is an intentional edit. Blank still isn't treated as
          // deletion during normal live counting; audited correction owns clearing.
          if (!meaningful(newValue) && meaningful(oldValue)) {
            merged[field] = oldValue;
            mergedVersions[field] = oldVersion || newVersion;
          } else {
            merged[field] = newValue;
            mergedVersions[field] = newVersion;
          }
          return;
        }
      }

      // Legacy payloads without field versions retain the original non-destructive
      // merge rule while they self-migrate on the next edit.
      if (!meaningful(newValue) && meaningful(oldValue)) merged[field] = oldValue;
    });
    if (Object.keys(mergedVersions).length) merged._mcFieldVersions = mergedVersions;
    return merged;
  });

  const existingTerminal = isTerminalSessionPayload(existing);
  const incomingTerminal = isTerminalSessionPayload(incoming);
  const mergedMeta = { ...(existing?.meta || {}), ...(incoming?.meta || {}) };
  if (existingTerminal && incomingTerminal) {
    // Keep the first terminal timestamps if a later completion retry arrives.
    mergedMeta.sectionCompletedAt = existing?.meta?.sectionCompletedAt || incoming?.meta?.sectionCompletedAt || null;
    mergedMeta.completedAt = existing?.meta?.completedAt || incoming?.meta?.completedAt || mergedMeta.sectionCompletedAt || null;
  }

  const touched = mergedLines.filter((line) => meaningful(line?.counted) || meaningful(line?.waste) || meaningful(line?.note)).length;
  const total = mergedLines.length;
  mergedMeta.liveRevision = Math.max(Number(existing?.meta?.liveRevision || 0), Number(incoming?.meta?.liveRevision || 0));
  mergedMeta.completedLines = Math.max(Number(mergedMeta.completedLines || 0), touched);
  mergedMeta.totalLines = Math.max(Number(mergedMeta.totalLines || 0), total);
  if (mergedMeta.sessionSummary && typeof mergedMeta.sessionSummary === "object") {
    mergedMeta.sessionSummary = {
      ...mergedMeta.sessionSummary,
      completedLines: Math.max(Number(mergedMeta.sessionSummary.completedLines || 0), touched),
      totalLines: Math.max(Number(mergedMeta.sessionSummary.totalLines || 0), total),
    };
  }

  return JSON.stringify({
    ...existing,
    ...incoming,
    meta: mergedMeta,
    lines: mergedLines,
    savedAt: incoming?.savedAt || existing?.savedAt || new Date().toISOString(),
  });
}

function sessionListCloudValueContainsIncoming(persistedValue = "", incomingValue = "") {
  const persistedRows = activeSessionArrayFromValue(persistedValue);
  const incomingRows = activeSessionArrayFromValue(incomingValue);

  // mc-session-list is transactionally MERGED in Firestore, not replaced.
  // Therefore exact string equality is not a valid durability acknowledgement:
  // Firestore may correctly contain older/live sessions in addition to the new row.
  // A prepare is durable once every active session submitted by this write is present
  // in the canonical cloud index for the active organisation.
  const persistedIds = new Set(
    persistedRows
      .filter((row) => sessionRecordBelongsToActiveOrg(row))
      .map((row) => String(row?.sessionId || "").trim())
      .filter(Boolean)
  );
  return incomingRows.every((row) => {
    const id = String(row?.sessionId || "").trim();
    return Boolean(id && persistedIds.has(id));
  });
}

function sessionCloudValueContainsIncoming(persistedValue = "", incomingValue = "") {
  const persisted = safeJsonParse(persistedValue, null);
  const incoming = safeJsonParse(incomingValue, null);
  if (!persisted || !incoming || !Array.isArray(incoming?.lines) || !Array.isArray(persisted?.lines)) return String(persistedValue ?? "") === String(incomingValue ?? "");
  if (isTerminalSessionPayload(incoming) && !isTerminalSessionPayload(persisted)) return false;
  const persistedById = new Map(persisted.lines.map((line, index) => [String(line?.id ?? line?.sku ?? index), line]));
  const meaningful = (value) => value !== null && value !== undefined && String(value).trim() !== "";
  return incoming.lines.every((line, index) => {
    const id = String(line?.id ?? line?.sku ?? index);
    const cloudLine = persistedById.get(id);
    if (!cloudLine) return false;
    return ["counted", "waste", "note"].every((field) => {
      if (!meaningful(line?.[field])) return true;
      const incomingVersion = line?._mcFieldVersions?.[field];
      const cloudVersion = cloudLine?._mcFieldVersions?.[field];
      if (incomingVersion && cloudVersion && compareFieldVersions(cloudVersion, incomingVersion) > 0) return true;
      return String(cloudLine?.[field] ?? "") === String(line?.[field] ?? "");
    });
  });
}

let sessionListWriteChain = Promise.resolve();

async function writeMcDoc(key, value, options = {}) {
  const stringKey = String(key || "");
  const stringValue = String(value || "");
  const bypassHydrationGate = options?.bypassHydrationGate === true;

  if (stringKey === "mc-profiles") {
    const requestedOrgId = activeOrgId;
    const run = () => {
      if (!requestedOrgId || activeOrgId !== requestedOrgId) throw new Error("The organisation changed before the People save completed.");
      return mutateProfileAuthority(stringValue);
    };
    profileWriteChain = profileWriteChain.catch(() => undefined).then(run);
    return profileWriteChain;
  }

  // MC434 — mc-session-list has several legitimate explicit lifecycle callers
  // (create/update, verify projection, finalize/archive). They all use the same
  // transactional merge document. When two explicit callers overlap in one tab,
  // Firestore can surface a failed-precondition/400 transaction conflict even
  // though a later retry succeeds. Serialize only this routing-index document
  // inside the client; canonical ledgers/details keep their independent writers.
  if (stringKey === "mc-session-list" && options?.sessionListSerialized !== true) {
    const run = () => writeMcDoc(stringKey, stringValue, { ...options, sessionListSerialized: true });
    sessionListWriteChain = sessionListWriteChain.catch(() => undefined).then(run);
    return sessionListWriteChain;
  }

  if (!activeOrgId || !isMcKey(stringKey)) {
    // Never queue shared MC data before the Firestore org is resolved. Browser
    // cache may still contain another org's state at this point.
    return;
  }

  if (!bypassHydrationGate && !activeSnapshotHydrated && !isCriticalSessionKey(stringKey)) {
    // Until the first snapshot for this org has hydrated, non-session local writes
    // are unsafe. Session actions are user-confirmed operational writes and must
    // persist immediately so refresh/access-layer sync cannot delete them.
    return;
  }

  const docId = docIdForLocalKey(stringKey);
  const ref = morpheusDocRef(activeOrgId, docId);
  let valueToWrite = stringValue;
  let nextJson = safeJsonParse(valueToWrite, null);

  if (stringKey === "mc-response-events") {
    // A browser can save investigation notes from a snapshot taken before a
    // server escalation. Retain the server timeline and stage receipts.
    let mergedRows = [];
    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(ref);
      const data = snapshot.exists() ? snapshot.data() || {} : {};
      const existing = profilesFromStoredValue(data.value || data.json);
      const byId = new Map(existing.map((event) => [event.id, event]));
      profilesFromStoredValue(stringValue).forEach((event) => {
        const previous = byId.get(event.id);
        const sameCompletion = previous && previous.sourceCompletedAt === event.sourceCompletedAt;
        if (!sameCompletion) { byId.set(event.id, event); return; }
        const auditEntries = (previous.timeline || []).filter((entry) => String(entry.id || "").startsWith("escalation:"));
        const timeline = new Map([...(event.timeline || []), ...auditEntries].map((entry) => [entry.id, entry]));
        const notifiedStages = { ...event.escalationPlan?.notifiedStages, ...previous.escalationPlan?.notifiedStages };
        const roleOrder = ["siteManager", "operationsManager", "owner"];
        const highestNotified = roleOrder.filter((role) => notifiedStages[role]).pop() || event.escalationPlan?.highestNotified || "";
        // Terminal state may only be superseded by a newer explicit action.
        const retainClosed = ["resolved", "dismissed"].includes(previous.status)
          && new Date(previous.updatedAt || 0).getTime() > new Date(event.updatedAt || 0).getTime();
        byId.set(event.id, { ...event,
          ...(retainClosed ? { status: previous.status, resolvedAt: previous.resolvedAt, resolvedBy: previous.resolvedBy, updatedAt: previous.updatedAt } : {}),
          escalatedAt: previous.escalatedAt || event.escalatedAt || null,
          timeline: [...timeline.values()],
          escalationPlan: { ...event.escalationPlan, notifiedStages, highestNotified },
        });
      });
      mergedRows = [...byId.values()].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)).slice(0, 2000);
      transaction.set(ref, { localKey: stringKey, value: JSON.stringify(mergedRows), json: mergedRows, updatedAt: serverTimestamp() }, { merge: true });
    });
    writeLocalFromCloud(stringKey, JSON.stringify(mergedRows));
    recordMcTrafficFirestoreWrite(stringKey, JSON.stringify(mergedRows));
    return;
  }

  if (isSessionStatePayloadKey(stringKey)) {
    // The old architecture replaced the entire session JSON document on every
    // autosave. That made multi-device counting fundamentally last-write-wins:
    // a stale browser could erase rows counted elsewhere or even reopen a
    // completed session. Merge the latest Firestore document transactionally.
    let committedTrafficValue = null;
    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(ref);
      const existingData = snapshot.exists() ? (snapshot.data() || {}) : {};
      const existingValue = cloudValueFromDocData(existingData) || "";
      recordMcTrafficFirestoreRead(stringKey, existingValue, "transaction", { documentData: existingData });
      const existingJson = safeJsonParse(existingValue, null);

      // P5.1 — Finalization Boundary. Once the canonical ledger is finalized it is
      // immutable to every ordinary session-state writer. Stale browsers, autosave,
      // compatibility mirrors and background sync may continue to fire, but none of
      // them are allowed to mutate the finalized operational result.
      if (/^mc-count-ledger:/i.test(stringKey) && (
        existingJson?.meta?.finalizedAt ||
        existingJson?.meta?.immutable === true ||
        String(existingJson?.meta?.lifecycleState || "").toLowerCase() === "finalized"
      )) {
        committedTrafficValue = null;
        return;
      }

      const mergedValue = mergeSessionPayloadValues(existingValue, stringValue);
      const mergedJson = safeJsonParse(mergedValue, null);
      const isCountLedger = /^mc-count-ledger:/i.test(stringKey);
      committedTrafficValue = mergedValue;
      transaction.set(ref, {
        localKey: stringKey,
        value: mergedValue,
        // MC488 — Count Ledger already has one canonical serialized representation
        // in `value`. Persisting the same 94+ row payload again as `json` roughly
        // doubles every write/read/listener document. Explicitly null the legacy
        // duplicate on the next ledger commit; session engine/checkpoint documents
        // keep their existing compatibility shape.
        ...(isCountLedger
          ? { json: null }
          : (mergedJson !== null ? { json: mergedJson } : {})),
        updatedAt: serverTimestamp(),
        updatedByEmail: activeUser.email || "",
        updatedByUid: activeUser.uid || "",
        writeMode: isCountLedger
          ? "transactional-count-ledger-single-payload-v1"
          : "transactional-session-state-v2",
      }, { merge: true });
    });
    if (committedTrafficValue !== null) {
      const committedTrafficJson = safeJsonParse(committedTrafficValue, null);
      const isCountLedger = /^mc-count-ledger:/i.test(stringKey);
      recordMcTrafficFirestoreWrite(stringKey, committedTrafficValue, {
        jsonValue: isCountLedger ? null : committedTrafficJson,
        documentData: {
          localKey: stringKey,
          value: committedTrafficValue,
          ...(isCountLedger
            ? { json: null }
            : (committedTrafficJson !== null ? { json: committedTrafficJson } : {})),
          updatedAt: "<serverTimestamp>",
          updatedByEmail: activeUser.email || "",
          updatedByUid: activeUser.uid || "",
          writeMode: isCountLedger
            ? "transactional-count-ledger-single-payload-v1"
            : "transactional-session-state-v2",
        },
      });
    }
    return;
  }

  if (stringKey === "mc-session-list") {
    // ROOT FIX: active sessions are created/updated by many devices at the same time.
    // A plain setDoc of the whole array is last-write-wins: an iPad can save Cashier,
    // then a laptop can save Storeroom from an older local copy and silently erase
    // Cashier from Firestore. That is why sessions disappeared and later had to be
    // recovered from their individual detail documents.
    //
    // Use a Firestore transaction and merge by sessionId against the latest cloud
    // value. Archive/finalize still wins via the archive list / transition overrides.
    const incomingRows = stampSessionRowsForActiveOrg(activeSessionArrayFromValue(stringValue));
    let committedTrafficValue = stringValue;
    await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(ref);
      const existingData = snapshot.exists() ? (snapshot.data() || {}) : {};
      const existingValue = cloudValueFromDocData(existingData) || "[]";
      recordMcTrafficFirestoreRead(stringKey, existingValue, "transaction", { documentData: existingData });
      let archiveValue = "[]";
      try {
        archiveValue = window.localStorage?.getItem("mc-session-archive") ||
          window.sessionStorage?.getItem("mc-session-archive") || "[]";
      } catch (_) {}

      const mergedRows = mergeActiveSessionListValues(
        existingValue,
        JSON.stringify(incomingRows),
        archiveValue
      );
      const mergedValue = JSON.stringify(mergedRows);
      committedTrafficValue = mergedValue;

      transaction.set(ref, {
        localKey: stringKey,
        value: mergedValue,
        json: mergedRows,
        updatedAt: serverTimestamp(),
        updatedByEmail: activeUser.email || "",
        updatedByUid: activeUser.uid || "",
        writeMode: "transactional-session-merge-v1",
      }, { merge: true });
    });
    const committedTrafficJson = safeJsonParse(committedTrafficValue, []);
    recordMcTrafficFirestoreWrite(stringKey, committedTrafficValue, {
      jsonValue: committedTrafficJson,
      documentData: {
        localKey: stringKey,
        value: committedTrafficValue,
        json: committedTrafficJson,
        updatedAt: "<serverTimestamp>",
        updatedByEmail: activeUser.email || "",
        updatedByUid: activeUser.uid || "",
        writeMode: "transactional-session-merge-v1",
      },
    });
    return;
  }

  // Large append/index authorities use the serialized `value` as their canonical
  // representation. Storing the same payload again as parsed `json` doubles every
  // subsequent read. MC496 extends the existing Storage Movement protection to the
  // shared Archive index: archive detail lives in per-session/final-snapshot docs,
  // while mc-session-archive is only a compact routing/summary index.
  const isStorageMovementLedgerKey = /^mc-storage-movement-(?:ledger:v1|head:v2|revision:v2):/i.test(stringKey);
  // Site sync histories also grow with every successful import. The serialized
  // `value` is authoritative; keeping a second parsed `json` copy doubles the
  // Firestore document and can push older sites toward the 1 MiB limit.
  const isStorageSyncHistoryKey = /^mc-storage-sync-activity:v1:/i.test(stringKey);
  // The 2,500-event operational activity authority is another growing array.
  // Keep its value for all existing readers without duplicating it as json.
  const isOperationalActivityKey = stringKey === "mc-operational-activity-v1";
  const isArchiveIndexKey = stringKey === "mc-session-archive";
  const isSinglePayloadKey = isStorageMovementLedgerKey || isStorageSyncHistoryKey || isOperationalActivityKey || isArchiveIndexKey;
  const singlePayloadWriteMode = isArchiveIndexKey
    ? "archive-index-single-payload-v2"
    : isStorageSyncHistoryKey
      ? "storage-sync-history-single-payload-v1"
    : isOperationalActivityKey
      ? "operational-activity-single-payload-v1"
    : /^mc-storage-movement-head:v2:/i.test(stringKey)
      ? "storage-movement-head-single-payload-v2"
      : /^mc-storage-movement-revision:v2:/i.test(stringKey)
        ? "storage-movement-revision-single-payload-v2"
        : "storage-ledger-single-payload-v1";

  await setDoc(
    ref,
    {
      localKey: stringKey,
      value: valueToWrite,
      json: isSinglePayloadKey ? null : nextJson,
      updatedAt: serverTimestamp(),
      updatedByEmail: activeUser.email || "",
      updatedByUid: activeUser.uid || "",
      ...(isSinglePayloadKey ? { writeMode: singlePayloadWriteMode } : {}),
    },
    { merge: true }
  );
  recordMcTrafficFirestoreWrite(stringKey, valueToWrite, {
    jsonValue: isSinglePayloadKey ? null : nextJson,
    documentData: {
      localKey: stringKey,
      value: valueToWrite,
      json: isSinglePayloadKey ? null : nextJson,
      updatedAt: "<serverTimestamp>",
      updatedByEmail: activeUser.email || "",
      updatedByUid: activeUser.uid || "",
      ...(isSinglePayloadKey ? { writeMode: singlePayloadWriteMode } : {}),
    },
  });
}

async function deleteMcDoc(key) {
  if (!activeOrgId || !isMcKey(key)) return;
  const stringKey = String(key || "");
  const docId = docIdForLocalKey(stringKey);
  recordMcTrafficWriteAttempt(stringKey, 1);
  try {
    await deleteDoc(morpheusDocRef(activeOrgId, docId));
    recordMcTrafficFirestoreDelete(stringKey);
  } catch (err) {
    recordMcTrafficWriteFailure(stringKey, { phase: "write", attempt: 1 });
    throw err;
  }
}

function isFirestoreBackpressureError(err) {
  const code = String(err?.code || '').toLowerCase();
  const message = String(err?.message || err || '').toLowerCase();
  return code.includes('resource-exhausted')
    || code.includes('failed-precondition')
    || code.includes('unavailable')
    || message.includes('maximum allowed queued writes')
    || message.includes('client is offline')
    || message.includes('transport errored');
}

function resumeScheduledWritePump(delayMs = 0) {
  if (scheduledWritePumpRunning || !scheduledWriteValues.size || !activeOrgId) return;
  if (scheduledWriteResumeTimer) return;
  scheduledWriteResumeTimer = setTimeout(() => {
    scheduledWriteResumeTimer = null;
    pumpScheduledFirestoreWrites();
  }, Math.max(0, Number(delayMs) || 0));
}

async function pumpScheduledFirestoreWrites() {
  if (scheduledWritePumpRunning || !activeOrgId || !scheduledWriteValues.size) return;
  scheduledWritePumpRunning = true;
  let backpressured = false;
  try {
    while (scheduledWriteValues.size && activeOrgId) {
      const first = scheduledWriteValues.entries().next().value;
      if (!first) break;
      const [stringKey, stringValue] = first;
      scheduledWriteValues.delete(stringKey);
      try {
        recentLocalWrites.set(stringKey, Date.now());
        await writeMcDoc(stringKey, stringValue);
        scheduledWriteFailureStreak = 0;
      } catch (err) {
        scheduledWriteFailureStreak += 1;
        // Keep only the latest value for this key. Do not immediately retry a
        // saturated Firestore client; its own transport already performs retry/
        // reconnect. A slow retry here prevents a write storm from starving
        // explicit Verify/Finalize transactions.
        if (!scheduledWriteValues.has(stringKey)) scheduledWriteValues.set(stringKey, stringValue);
        dispatchReady(activeOrgId, !!activeOrgId, { lastError: String(err?.message || err) });
        if (isFirestoreBackpressureError(err)) {
          backpressured = true;
          console.warn('Morpheus Count background sync paused for Firestore backpressure', { key: stringKey, code: err?.code || '', message: err?.message || String(err) });
          break;
        }
        console.error('Morpheus Count Firestore write failed', {
          key: stringKey,
          docId: docIdForLocalKey(stringKey),
          orgId: activeOrgId,
          path: activeOrgId ? `orgs/${activeOrgId}/morpheus/${docIdForLocalKey(stringKey)}` : 'NO_ORG_ID',
          err,
        });
        break;
      }
    }
  } finally {
    scheduledWritePumpRunning = false;
    // Failed writes remain queued, but a persistent Firestore 400/error must
    // not trigger a new failed request every 250 ms and starve site revisions.
    if (scheduledWriteValues.size) resumeScheduledWritePump(backpressured
      ? 5000
      : Math.min(30000, 1000 * Math.pow(2, Math.min(scheduledWriteFailureStreak, 5))));
  }
}

function scheduleFirestoreWrite(key, value) {
  if (applyingCloud || !isMcKey(key) || isLocalOnlyMcKey(key)) return;
  const stringKey = String(key || '');
  const stringValue = String(value ?? '');
  if (!activeOrgId) return;

  // Canonical ledgers/final snapshots AND shared session routing lists are
  // persisted by explicit durable writers in mcEngineData/live-count lifecycle.
  // Never enqueue a second localStorage-bridge transaction for the same document.
  // MC430: mc-session-list was still double-written after MC429 and was the source
  // of the remaining 409/resource-exhausted storm that made Verify appear hung.
  if (isExplicitCloudOwnedKey(stringKey)) {
    scheduledWriteValues.delete(stringKey);
    pendingWrites.delete(stringKey);
    return;
  }

  // User changes made while the organisation snapshot is still hydrating must not
  // be discarded. Keep the latest value in memory and flush it as soon as the
  // snapshot becomes authoritative.
  if (!activeSnapshotHydrated && !isCriticalSessionKey(stringKey)) {
    pendingWrites.set(stringKey, stringValue);
    recentLocalWrites.set(stringKey, Date.now());
    return;
  }

  recentLocalWrites.set(stringKey, Date.now());
  scheduledWriteValues.set(stringKey, stringValue);
  resumeScheduledWritePump(0);
}

function scheduleFirestoreDelete(key) {
  if (applyingCloud || !isMcKey(key) || isLocalOnlyMcKey(key)) return;
  const stringKey = String(key || "");

  // MC477 — profile authority is durable organisation data, never a cache.
  // Individual people are removed by saving the updated profile array; there is
  // no valid browser-cache lifecycle that should delete the canonical profiles
  // document itself. A generic localStorage.removeItem("mc-profiles") must
  // therefore remain local-only and can never propagate to Firestore.
  if (stringKey === "mc-profiles") {
    console.warn("Morpheus Count blocked deletion of canonical profile authority", {
      orgId: activeOrgId,
      key: stringKey,
      docId: docIdForLocalKey(stringKey),
    });
    return;
  }

  deleteMcDoc(stringKey).catch((err) => {
    console.error("Morpheus Count Firestore delete failed", {
      key,
      docId: docIdForLocalKey(key),
      orgId: activeOrgId,
      err,
    });
  });
}

function flushPendingWrites() {
  if (!activeOrgId || !pendingWrites.size) return;
  const entries = Array.from(pendingWrites.entries());
  pendingWrites.clear();
  entries.forEach(([key, value]) => scheduleFirestoreWrite(key, value));
}

function installLocalStorageBridge() {
  if (typeof window === "undefined" || bridgeInstalled) return;
  bridgeInstalled = true;
  originalSetItem = Storage.prototype.setItem;
  originalRemoveItem = Storage.prototype.removeItem;
  initializeMorpheusStorageEngine().then((state) => {
    if (state?.ready && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("mc-storage-engine-ready", { detail: state }));
    }
  }).catch((error) => {
    console.warn("Morpheus IndexedDB storage engine could not initialize", error);
  });

  Storage.prototype.setItem = function patchedSetItem(key, value) {
    let result;
    let previousValue = null;
    try { previousValue = this.getItem(key); } catch (_) {}
    try {
      result = originalSetItem.call(this, key, value);
    } catch (error) {
      const isQuota = error?.name === "QuotaExceededError" || /quota/i.test(String(error?.message || ""));
      if (this !== window.localStorage || !isQuota) throw error;

      // Keep enough synchronous localStorage headroom for Firebase auth and small
      // preferences. Large Morpheus cache payloads are archived to IndexedDB.
      const recovery = freeLocalStorageForCriticalWrite(Math.max(1024 * 1024, String(value || "").length * 2));
      if (!recovery.removed) throw error;
      result = originalSetItem.call(this, key, value);
      console.info("Morpheus storage engine released localStorage capacity", recovery);
    }
    if (this === window.localStorage && isMcKey(key)) {
      // Re-render/projection code frequently re-writes identical JSON. Treat that
      // as a cache no-op instead of another cloud mutation.
      if (String(previousValue ?? '') !== String(value ?? '')) {
        scheduleFirestoreWrite(String(key), String(value));
        dispatchStorageLikeEvent();
      }
    }
    return result;
  };

  Storage.prototype.removeItem = function patchedRemoveItem(key) {
    const result = originalRemoveItem.call(this, key);
    if (this === window.localStorage && isMcKey(key)) {
      // MC498 — localStorage deletion is ALWAYS cache eviction, never permission
      // to delete the organisation's Firestore document. Global CSV imports
      // clear old browser aliases (dozens per upload); the previous guard
      // accidentally scheduled cloud deletes for every non-lifecycle alias,
      // creating massive write contention during inventory uploads.
      // The intentional cloud delete contract is mcRemoveItem() or a dedicated
      // lifecycle API, which explicitly calls scheduleFirestoreDelete().
      dispatchStorageLikeEvent();
    }
    return result;
  };
}

async function writeMeta() {
  if (!activeOrgId) return;
  try {
    await setDoc(morpheusDocRef(activeOrgId, "meta"), {
      app: "morpheus-count",
      mode: "firestore-org-shared-source-of-truth",
      structure: ["meta", "profiles", "sessions", "archives", "engine_ALL"],
      updatedAt: serverTimestamp(),
      updatedByEmail: activeUser.email || "",
      updatedByUid: activeUser.uid || "",
    }, { merge: true });
  } catch (err) {
    console.error("Morpheus Count meta write failed", err);
  }
}

export function isMcCloudSyncReady() {
  if (typeof window === "undefined") return false;
  return !!window.__morpheusSyncState?.ready;
}

export function getMcCloudSyncState() {
  if (typeof window === "undefined") return { orgId: "", ready: false, path: "" };
  return window.__morpheusSyncState || {
    orgId: activeOrgId || "",
    ready: Boolean(activeOrgId && activeSnapshotHydrated),
    path: activeOrgId ? `orgs/${activeOrgId}/morpheus` : "",
    phase: activeSnapshotHydrated ? "ready" : "hydrating",
    dataHydrated: Boolean(activeSnapshotHydrated),
    profilesHydrated: Boolean(activeSnapshotHydrated),
  };
}

export function getMcActiveOrgId() {
  return activeOrgId || "";
}



export function mcGetStoragePressureReport() {
  if (typeof window === "undefined") return { localKeys: 0, sessionKeys: 0, mcLocalKeys: 0, mcSessionKeys: 0, removableLocalKeys: 0, removableSessionKeys: 0, localBytes: 0, sessionBytes: 0, removableBytes: 0 };
  const isRemovable = (key = "") => {
    const clean = String(key || "");
    return /^mc-session-engine[:\-]/i.test(clean)
      || /^mc-live-update[:\-]/i.test(clean)
      || /^mc-link-inventory-snapshot(?::|$)/i.test(clean)
      || /^mc-import-active(?::|$)/i.test(clean)
      || /^mc-active-import-lock(?::|$)/i.test(clean)
      || /^mc-latest-import-lock(?::|$)/i.test(clean)
      || clean === "mc-activity-log"
      || clean === "mc-warm-cache-state";
  };
  const scan = (storage) => {
    const out = { keys: 0, mcKeys: 0, removableKeys: 0, bytes: 0, removableBytes: 0 };
    if (!storage) return out;
    try {
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i) || "";
        const value = storage.getItem(key) || "";
        const size = key.length + value.length;
        out.keys += 1;
        out.bytes += size;
        if (/^mc-/i.test(key) || key.includes("mc")) out.mcKeys += 1;
        if (isRemovable(key)) {
          out.removableKeys += 1;
          out.removableBytes += size;
        }
      }
    } catch (_) {}
    return out;
  };
  const local = scan(window.localStorage);
  const session = scan(window.sessionStorage);
  return {
    localKeys: local.keys,
    sessionKeys: session.keys,
    mcLocalKeys: local.mcKeys,
    mcSessionKeys: session.mcKeys,
    removableLocalKeys: local.removableKeys,
    removableSessionKeys: session.removableKeys,
    localBytes: local.bytes,
    sessionBytes: session.bytes,
    removableBytes: local.removableBytes + session.removableBytes,
  };
}

export function mcClearOperationalCache() {
  if (typeof window === "undefined") return { removedLocal: 0, removedSession: 0, removedBytes: 0 };
  const shouldRemove = (key = "") => {
    const clean = String(key || "");
    return /^mc-session-engine[:\-]/i.test(clean)
      || /^mc-live-update[:\-]/i.test(clean)
      || /^mc-link-inventory-snapshot(?::|$)/i.test(clean)
      || /^mc-import-active(?::|$)/i.test(clean)
      || /^mc-active-import-lock(?::|$)/i.test(clean)
      || /^mc-latest-import-lock(?::|$)/i.test(clean)
      || clean === "mc-activity-log"
      || clean === "mc-warm-cache-state";
  };
  const cleanStorage = (storage, useOriginalRemove = false) => {
    const keys = [];
    let bytes = 0;
    try {
      for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i) || "";
        if (shouldRemove(key)) {
          keys.push(key);
          try { bytes += key.length + String(storage.getItem(key) || "").length; } catch (_) {}
        }
      }
      keys.forEach((key) => {
        try {
          if (useOriginalRemove && originalRemoveItem) originalRemoveItem.call(storage, key);
          else storage.removeItem(key);
        } catch (_) {}
      });
    } catch (_) {}
    return { removed: keys.length, bytes };
  };
  const local = cleanStorage(window.localStorage, true);
  const session = cleanStorage(window.sessionStorage, false);
  dispatchStorageLikeEvent();
  return { removedLocal: local.removed, removedSession: session.removed, removedBytes: local.bytes + session.bytes };
}


export async function mcListCloudCountLedgers() {
  // P5.3 durability guard: Access Layer / org re-hydration intentionally clears
  // browser caches. Session discovery therefore cannot depend on locally cached
  // mc-count-ledger keys. Query the active org's Morpheus collection directly and
  // return canonical ledger documents as a READ-ONLY discovery source.
  if (!activeOrgId) return [];
  try {
    const snap = await getDocs(collection(db, "orgs", activeOrgId, "morpheus"));
    snap.docs.forEach((docSnap) => {
      const data = docSnap?.data?.() || {};
      const localKey = localKeyForDoc(docSnap?.id || "", data) || docSnap?.id || "unknown";
      recordMcTrafficFirestoreRead(`collection:morpheus:count-ledger-discovery:${localKey}`, cloudValueFromDocData(data) || "", "direct", { documentData: data });
    });
    return snap.docs
      .map((docSnap) => {
        if (!docSnap || docSnap.id === "meta") return null;
        const data = docSnap.data?.() || {};
        const localKey = localKeyForDoc(docSnap.id, data);
        if (!/^mc-count-ledger:/i.test(String(localKey || ""))) return null;
        const value = cloudValueFromDocData(data);
        if (!value) return null;
        const sessionId = String(localKey || "").replace(/^mc-count-ledger:/i, "").trim();
        if (!sessionId) return null;
        return {
          localKey,
          sessionId,
          value,
          updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() || "",
        };
      })
      .filter(Boolean);
  } catch (err) {
    console.warn("Morpheus Count could not list canonical count ledgers", err);
    return [];
  }
}


export async function mcListCloudSessionDetails() {
  // Session recovery must not depend on the bulk organisation snapshot finishing.
  // Sessions/detail documents are critical operational state and can be queried
  // directly as soon as the active organisation is resolved. The old hydration
  // gate left Sessions permanently empty whenever the bulk snapshot was delayed or
  // restarted, even though the live session detail documents already existed in
  // Firestore.
  if (!activeOrgId) return null;
  try {
    const snap = await getDocs(collection(db, "orgs", activeOrgId, "morpheus"));
    snap.docs.forEach((docSnap) => {
      const data = docSnap?.data?.() || {};
      const localKey = localKeyForDoc(docSnap?.id || "", data) || docSnap?.id || "unknown";
      recordMcTrafficFirestoreRead(`collection:morpheus:session-detail-discovery:${localKey}`, cloudValueFromDocData(data) || "", "direct", { documentData: data });
    });
    return snap.docs
      .map((docSnap) => {
        if (!docSnap || docSnap.id === "meta") return null;
        const data = docSnap.data?.() || {};
        const localKey = localKeyForDoc(docSnap.id, data);
        // Session recovery has three durable authority families. Current and
        // legacy live-count detail documents use mc-session-engine / mc-session-detail.
        // The preparation seed is also persisted per session and contains the full
        // preparedRows payload. It is the last trustworthy recovery authority when a
        // historical bug emptied both the canonical session list and the detail doc.
        // Archive/finalize state is still checked by the reconciliation layer before a
        // seed can ever be promoted back into Sessions.
        const key = String(localKey || "");
        const detailMatch = key.match(/^mc-session-(?:engine|detail)[:\-](.+)$/i);
        const seedMatch = key.match(/^mc-session-seed[:\-](.+)$/i);
        const ledgerMatch = key.match(/^mc-count-ledger[:\-](.+)$/i);
        const completedMatch = key.match(/^mc-session-completed[:\-](.+)$/i);
        const checkpointMatch = key.match(/^mc-session-checkpoint[:\-](.+)$/i);
        const finalMatch = key.match(/^mc-final-count-snapshot[:\-](.+)$/i);
        const archiveMatch = key.match(/^mc-archive-session[:\-](.+)$/i);
        const value = cloudValueFromDocData(data);
        if (!value) return null;
        let parsed = null;
        try { parsed = JSON.parse(value); } catch (_) {}
        let sessionId = detailMatch?.[1] || ledgerMatch?.[1] || completedMatch?.[1] || checkpointMatch?.[1] || finalMatch?.[1] || archiveMatch?.[1] || "";
        let authorityType = detailMatch ? "detail"
          : ledgerMatch ? "ledger"
          : completedMatch ? "completed"
          : checkpointMatch ? "checkpoint"
          : finalMatch ? "final"
          : archiveMatch ? "archive"
          : seedMatch ? "seed" : "payload";
        // Revisioned final snapshots are keyed <sessionId>:r<revision>. Terminal
        // reconciliation must compare against the base session id.
        if (finalMatch && sessionId) sessionId = String(sessionId).replace(/:r\d+$/i, "");
        if (seedMatch) sessionId = String(parsed?.sessionId || parsed?.meta?.sessionId || "").trim();
        if (!sessionId) sessionId = String(parsed?.meta?.sessionId || parsed?.sessionId || "").trim();
        if (!sessionId) return null;
        const hasRows = Array.isArray(parsed?.lines) && parsed.lines.length > 0;
        const hasPreparedRows = Array.isArray(parsed?.preparedRows) && parsed.preparedRows.length > 0;
        if (!detailMatch && !seedMatch && !ledgerMatch && !completedMatch && !checkpointMatch && !finalMatch && !archiveMatch && !hasRows && !hasPreparedRows) return null;
        return {
          localKey,
          sessionId,
          authorityType,
          value,
          updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() || "",
        };
      })
      .filter(Boolean);
  } catch (err) {
    console.warn("Morpheus Count could not list cloud session details", err);
    return [];
  }
}


// MC478 — bounded authority hydration.
// P1 correctly removed the full `morpheus` collection bootstrap, but several
// non-live workspaces still depended on that accidental cache fill. Restore the
// small canonical authorities explicitly (one getDoc per known authority) and
// keep snapshots/history/session-detail documents asleep until their owners ask.
const MC_BOOT_AUTHORITY_KEYS = [
  "mc-profiles", "mc-sections", "mc-cycles", "mc-site-responsibility-backup", "mc-role-escalation-policy-v1", "mc-sites",
  "mc-csv-site-resolution", "mc-logic-pass", "mc-import-active",
  "mc-shared-category-groups", "mc-shared-category-ownership",
  "mc-shared-category-order", "mc-shared-unit-intelligence",
  "mc-unit-intelligence-config", "mc-category-settings-backup",
  "mc-inventory-review-decisions-v4", "mc-flag-config-shared",
  // Core Config and Response Design are compact shared documents containing the
  // All Sites default plus sparse site overrides. One bounded getDoc per record
  // keeps devices aligned without realtime listeners or per-site bootstrap reads.
  "mc-response-design-config",
  "mc-pos-integration-settings", "mc-prepared-inventory",
  "mc-operational-activity-v1", "mc-activity-log",
  "mc-billing-snapshot", "mc-retained-billing-sites",
];

function authorityScopeCandidatesFromSitesRaw(raw = "") {
  const parsed = safeJsonParse(raw, null);
  const rows = Array.isArray(parsed) ? parsed
    : Array.isArray(parsed?.sites) ? parsed.sites
    : Array.isArray(parsed?.rows) ? parsed.rows : [];
  const scopes = new Set();
  rows.forEach((site) => {
    const candidates = [site?.inventoryKey, site?.inventory_key, site?.csvInventoryKey, site?.key, site?.name, site?.siteName];
    candidates.filter(Boolean).forEach((value) => {
      const normalized = normalizeImportScopeValue(value);
      if (normalized && normalized !== "ALL") scopes.add(normalized);
    });
  });
  return Array.from(scopes);
}

async function hydrateAuthorityKeysForOrg(orgId, keys = []) {
  const cleanOrg = String(orgId || "").trim();
  if (!cleanOrg || !Array.isArray(keys) || !keys.length) return { docs: [], applied: null };
  const ids = new Set();
  keys.forEach((key) => {
    ids.add(docIdForLocalKey(key));
    // A few early Morpheus writers used the literal mc-* key as the document id.
    // Reading both names is still bounded and lets us recover those authorities
    // without ever listing the collection.
    ids.add(String(key || ""));
  });
  const snaps = await Promise.all(Array.from(ids).map((id) =>
    getDoc(morpheusDocRef(cleanOrg, id)).catch((err) => {
      console.warn("Morpheus authority read failed", { orgId: cleanOrg, docId: id, err });
      return null;
    })
  ));
  const docs = snaps.filter((snap) => snap?.exists?.());
  const applied = applyMorpheusSnapshotToLocal({ docs }, { replaceCache: false });
  if (docs.length) dispatchStorageLikeEvent();
  return { docs, applied };
}

async function hydrateBoundedBootstrapAuthorities(orgId) {
  const first = await hydrateAuthorityKeysForOrg(orgId, MC_BOOT_AUTHORITY_KEYS);
  let sitesRaw = null;
  try { sitesRaw = mcGetItem("mc-sites"); } catch (_) {}
  const scopes = authorityScopeCandidatesFromSitesRaw(sitesRaw || "");
  const scopedKeys = [];
  scopes.forEach((scope) => {
    scopedKeys.push(
      `mc-logic-pass:${scope}`,
      `mc-import-active:${scope}`,
      `mc-pos-integration-settings:${scope}`,
      `mc-prepared-inventory:${scope}`,
      // MC499 — Storage Movement + Sync Activity are forensic/history authorities,
      // not bootstrap state. Hydrating every site's growing history at login was
      // silently downloading large documents even when the operator never opened
      // Intake/Storage Intelligence. Those histories are now explicitly on-demand.
    );
  });
  const second = scopedKeys.length ? await hydrateAuthorityKeysForOrg(orgId, scopedKeys) : { docs: [], applied: null };
  return { docs: [...first.docs, ...second.docs], scopes };
}

// MC Sites: load the authoritative recipient ledger explicitly on each Sites visit.
// Unlike the general cached read, this must not silently return a stale browser
// snapshot when an admin is inspecting WhatsApp routing on a second device.
export async function mcGetSiteResponsibilityAuthorities() {
  const orgId = activeOrgId;
  if (!orgId || !activeSnapshotHydrated) throw new Error("Morpheus organisation is still syncing.");
  const keys = ["mc-site-responsibility-backup", "mc-sites"];
  const result = {};
  await Promise.all(keys.map(async (key) => {
    const snap = await getDocFromServer(morpheusDocRef(orgId, docIdForLocalKey(key)));
    if (activeOrgId !== orgId) throw new Error("Organisation changed during the Sites refresh.");
    const data = snap.exists() ? (snap.data() || {}) : {};
    const value = snap.exists() ? cloudValueFromDocData(data) : null;
    recordMcTrafficFirestoreRead(key, value || "", "direct", { documentData: data });
    result[key] = value;
  }));
  return { orgId, responsibility: result["mc-site-responsibility-backup"], sites: result["mc-sites"] };
}

// Update just the chosen person/role in Firestore. Two documents are updated in
// one transaction so a stale device cannot replace the other sites' assignees
// or leave the WhatsApp routing registry inconsistent with the Sites UI.
export async function mcPatchSiteResponsibility(siteId, field, personId, expectedOrgId) {
  const orgId = activeOrgId;
  const validFields = ["siteManagerPersonId", "operationsManagerPersonId", "ownerPersonId"];
  if (!orgId || !activeSnapshotHydrated || orgId !== expectedOrgId) {
    throw new Error("Morpheus organisation is not ready or has changed.");
  }
  if (!siteId || !validFields.includes(field)) throw new Error("Invalid site responsibility update.");
  const responsibilityKey = "mc-site-responsibility-backup";
  const sitesKey = "mc-sites";
  const responsibilityRef = morpheusDocRef(orgId, docIdForLocalKey(responsibilityKey));
  const sitesRef = morpheusDocRef(orgId, docIdForLocalKey(sitesKey));
  const stamp = new Date().toISOString();
  let result = null;

  await runTransaction(db, async (transaction) => {
    // All reads must come before all writes in a Firestore transaction.
    const responsibilityDoc = await transaction.get(responsibilityRef);
    const sitesDoc = await transaction.get(sitesRef);
    const backup = safeJsonParse(responsibilityDoc.exists() ? cloudValueFromDocData(responsibilityDoc.data() || {}) : "", {});
    const remoteSites = safeJsonParse(sitesDoc.exists() ? cloudValueFromDocData(sitesDoc.data() || {}) : "", []);
    const nextBackup = backup && typeof backup === "object" && !Array.isArray(backup) ? { ...backup } : {};
    const rows = Array.isArray(remoteSites) ? remoteSites : [];
    const matchingSite = rows.find((item) => item?.siteId === siteId);
    if (!matchingSite) {
      throw new Error(`Site ${siteId} was not found in the shared registry. Refresh Sites before changing recipients.`);
    }
    const previous = nextBackup[siteId] && typeof nextBackup[siteId] === "object" ? nextBackup[siteId] : {};
    nextBackup[siteId] = {
      siteManagerPersonId: previous.siteManagerPersonId ?? matchingSite.siteManagerPersonId ?? "",
      operationsManagerPersonId: previous.operationsManagerPersonId ?? matchingSite.operationsManagerPersonId ?? "",
      ownerPersonId: previous.ownerPersonId ?? matchingSite.ownerPersonId ?? "",
      ...previous,
      [field]: String(personId || ""),
      updatedAt: stamp,
    };
    const nextSites = rows.map((item) => item?.siteId === siteId
      ? { ...item, ...Object.fromEntries(validFields.map((key) => [key, nextBackup[siteId][key] || ""])), updatedAt: stamp }
      : item);
    const nextBackupRaw = JSON.stringify(nextBackup);
    const nextSitesRaw = JSON.stringify(nextSites);
    transaction.set(responsibilityRef, {
      localKey: responsibilityKey, value: nextBackupRaw, json: null, updatedAt: serverTimestamp(),
      updatedByEmail: activeUser.email || "", updatedByUid: activeUser.uid || "",
    }, { merge: true });
    transaction.set(sitesRef, {
      localKey: sitesKey, value: nextSitesRaw, json: null, updatedAt: serverTimestamp(),
      updatedByEmail: activeUser.email || "", updatedByUid: activeUser.uid || "",
    }, { merge: true });
    result = { orgId, responsibility: nextBackupRaw, sites: nextSitesRaw };
  });
  if (activeOrgId !== orgId) throw new Error("Organisation changed before the responsibility save completed.");
  recordMcTrafficFirestoreWrite(responsibilityKey, result.responsibility);
  recordMcTrafficFirestoreWrite(sitesKey, result.sites);
  return result;
}

// The escalation policy governs notification timing and channels across every
// device. It must be read from the server, not from a browser's local cache.
const ROLE_ESCALATION_POLICY_KEY = "mc-role-escalation-policy-v1";
const ROLE_ESCALATION_KEYS = ["siteManager", "operationsManager", "owner"];
const ROLE_ESCALATION_DEFAULTS = {
  siteManager: { delayMinutes: 0, channels: ["email", "whatsapp"] },
  operationsManager: { delayMinutes: 15, channels: ["email", "whatsapp"], minSeverity: "default" },
  owner: { delayMinutes: 60, channels: ["email", "whatsapp"], minSeverity: "default" },
};

function normalizeSharedEscalationValue(value) {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  return Object.fromEntries(ROLE_ESCALATION_KEYS.map((key) => {
    const current = source[key] && typeof source[key] === "object" ? source[key] : {};
    const delay = Number(current.delayMinutes);
    const channels = Array.isArray(current.channels)
      ? [...new Set(current.channels.filter((channel) => channel === "email" || channel === "whatsapp"))]
      : ROLE_ESCALATION_DEFAULTS[key].channels;
    return [key, {
      delayMinutes: current.delayMinutes !== null && current.delayMinutes !== undefined && Number.isFinite(delay)
        ? Math.max(0, Math.min(10080, delay))
        : ROLE_ESCALATION_DEFAULTS[key].delayMinutes,
      channels,
      ...(key !== "siteManager" ? {
        minSeverity: ["default", "low", "medium", "high", "critical"].includes(current.minSeverity)
          ? current.minSeverity : "default",
      } : {}),
    }];
  }));
}

function requireEscalationOrg(expectedOrgId) {
  const orgId = String(activeOrgId || "").trim();
  if (!orgId || !activeSnapshotHydrated || orgId !== String(expectedOrgId || "").trim()) {
    throw new Error("Organisation is not ready or changed. Refresh Escalation before editing.");
  }
  return orgId;
}

export async function mcGetRoleEscalationPolicyAuthority() {
  const orgId = String(activeOrgId || "").trim();
  if (!orgId || !activeSnapshotHydrated) throw new Error("Organisation is still syncing. Try again after it loads.");
  const ref = morpheusDocRef(orgId, docIdForLocalKey(ROLE_ESCALATION_POLICY_KEY));
  const snap = await getDocFromServer(ref);
  if (activeOrgId !== orgId) throw new Error("Organisation changed during Escalation refresh.");
  const data = snap.exists() ? snap.data() || {} : {};
  const value = snap.exists() ? cloudValueFromDocData(data) : null;
  recordMcTrafficFirestoreRead(ROLE_ESCALATION_POLICY_KEY, value || "", "direct", { documentData: data });
  return { orgId, exists: snap.exists(), value };
}

// A transaction merges only the chosen role into the latest server policy.
// Changing Owner on device B cannot put device A's older Operations delay back.
export async function mcPatchRoleEscalationPolicy(roleKey, patch, expectedOrgId) {
  const orgId = requireEscalationOrg(expectedOrgId);
  if (!ROLE_ESCALATION_KEYS.includes(roleKey) || !patch || typeof patch !== "object") {
    throw new Error("Invalid escalation policy change.");
  }
  const fields = Object.keys(patch);
  if (!fields.length || fields.some((field) => field !== "delayMinutes" && field !== "channels" && field !== "minSeverity")) {
    throw new Error("Invalid escalation policy fields.");
  }
  if (Object.prototype.hasOwnProperty.call(patch, "delayMinutes") &&
      (!Number.isFinite(Number(patch.delayMinutes)) || Number(patch.delayMinutes) < 0)) {
    throw new Error("Escalation delay must be a non-negative number of minutes.");
  }
  if (Object.prototype.hasOwnProperty.call(patch, "channels") &&
      (!Array.isArray(patch.channels) || patch.channels.some((channel) => !["email", "whatsapp"].includes(channel)))) {
    throw new Error("Invalid escalation channel.");
  }
  if (Object.prototype.hasOwnProperty.call(patch, "minSeverity") &&
      (roleKey === "siteManager" || !["default", "low", "medium", "high", "critical"].includes(patch.minSeverity))) {
    throw new Error("Invalid escalation severity threshold.");
  }
  const ref = morpheusDocRef(orgId, docIdForLocalKey(ROLE_ESCALATION_POLICY_KEY));
  // No background localStorage bridge may replay a stale whole-policy snapshot.
  pendingWrites.delete(ROLE_ESCALATION_POLICY_KEY);
  scheduledWriteValues.delete(ROLE_ESCALATION_POLICY_KEY);
  const value = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error("Shared policy has not been published. Publish this device's policy first.");
    const raw = cloudValueFromDocData(snap.data() || {});
    const current = normalizeSharedEscalationValue(safeJsonParse(raw, {}));
    const updated = normalizeSharedEscalationValue({
      ...current,
      [roleKey]: { ...current[roleKey], ...patch },
    });
    const nextValue = JSON.stringify(updated);
    tx.set(ref, {
      localKey: ROLE_ESCALATION_POLICY_KEY, value: nextValue, json: null,
      updatedAt: serverTimestamp(), updatedByEmail: activeUser.email || "", updatedByUid: activeUser.uid || "",
    }, { merge: true });
    return nextValue;
  });
  if (activeOrgId !== orgId) throw new Error("Organisation changed before the policy save finished.");
  recordMcTrafficFirestoreWrite(ROLE_ESCALATION_POLICY_KEY, value);
  return { orgId, value };
}

// Recovery for an organisation whose policy only ever existed on one browser.
// Creates the cloud document ONLY if it is absent; never overwrites an existing
// policy configured by another device.
export async function mcPublishRoleEscalationPolicyIfMissing(policy, expectedOrgId) {
  const orgId = requireEscalationOrg(expectedOrgId);
  const ref = morpheusDocRef(orgId, docIdForLocalKey(ROLE_ESCALATION_POLICY_KEY));
  const requested = JSON.stringify(normalizeSharedEscalationValue(policy));
  pendingWrites.delete(ROLE_ESCALATION_POLICY_KEY);
  scheduledWriteValues.delete(ROLE_ESCALATION_POLICY_KEY);
  const result = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (snap.exists()) return { created: false, value: cloudValueFromDocData(snap.data() || {}) };
    tx.set(ref, {
      localKey: ROLE_ESCALATION_POLICY_KEY, value: requested, json: null,
      updatedAt: serverTimestamp(), updatedByEmail: activeUser.email || "", updatedByUid: activeUser.uid || "",
    });
    return { created: true, value: requested };
  });
  if (activeOrgId !== orgId) throw new Error("Organisation changed before the policy publication finished.");
  if (result.created) recordMcTrafficFirestoreWrite(ROLE_ESCALATION_POLICY_KEY, requested);
  return { orgId, ...result };
}

export async function mcGetCloudItem(key, options = {}) {
  const stringKey = String(key || "");
  if (!activeOrgId || !isMcKey(stringKey)) {
    if (options.throwOnError === true) throw new Error("Morpheus organisation is not ready for a confirmed cloud read.");
    return null;
  }
  try {
    const ref = morpheusDocRef(activeOrgId, docIdForLocalKey(stringKey));
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      recordMcTrafficFirestoreRead(stringKey, "", "direct");
      return null;
    }
    const data = snap.data() || {};
    const value = typeof data.value === "string"
      ? data.value
      : data.json !== undefined
        ? JSON.stringify(data.json)
        : null;
    recordMcTrafficFirestoreRead(stringKey, value || "", "direct", { documentData: data });
    return value;
  } catch (err) {
    console.warn("Morpheus Count cloud item read failed", { key: stringKey, err });
    // Missing data and a failed network read are NOT equivalent. Import writers
    // must never mistake a failed read for an empty ledger and overwrite history.
    if (options.throwOnError === true) throw err;
    return null;
  }
}

export function mcGetItem(key) {
  if (typeof window === "undefined") return null;
  const stringKey = String(key);
  if (volatileMcCache.has(stringKey)) return volatileMcCache.get(stringKey);
  const localValue = window.localStorage.getItem(stringKey);
  if (localValue !== null) return localValue;
  try {
    return window.sessionStorage?.getItem(stringKey) ?? null;
  } catch (_) {
    return null;
  }
}

export function mcSetCachedItem(key, value) {
  if (typeof window === "undefined") return false;
  const stringKey = String(key);
  const stringValue = String(value);
  volatileMcCache.set(stringKey, stringValue);
  if (shouldUseIndexedDb(stringKey, stringValue)) {
    idbSetItem(stringKey, stringValue, { reason: "mc-cache" }).catch((error) => {
      console.warn("Morpheus IndexedDB cache write failed", { key: stringKey, error });
    });
    try {
      if (originalRemoveItem) originalRemoveItem.call(window.localStorage, stringKey);
      else window.localStorage.removeItem(stringKey);
    } catch (_) {}
    try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
    return true;
  }
  try {
    if (originalSetItem) originalSetItem.call(window.localStorage, stringKey, stringValue);
    else window.localStorage.setItem(stringKey, stringValue);
    try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
    return true;
  } catch (err) {
    if (err?.name !== "QuotaExceededError") throw err;
    clearMcStoragePressure();
    try {
      if (originalSetItem) originalSetItem.call(window.localStorage, stringKey, stringValue);
      else window.localStorage.setItem(stringKey, stringValue);
      try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
      return true;
    } catch (retryErr) {
      if (retryErr?.name !== "QuotaExceededError") throw retryErr;
      return writeSessionFallback(stringKey, stringValue);
    }
  }
}

export function mcSetItemSilent(key, value) {
  if (typeof window === "undefined") return;
  const stringKey = String(key);
  const stringValue = String(value);
  volatileMcCache.set(stringKey, stringValue);

  if (shouldUseIndexedDb(stringKey, stringValue)) {
    idbSetItem(stringKey, stringValue, { reason: "mc-shared-payload-silent" }).catch((error) => {
      console.warn("Morpheus IndexedDB silent shared payload write failed", { key: stringKey, error });
    });
    try {
      if (originalRemoveItem) originalRemoveItem.call(window.localStorage, stringKey);
      else window.localStorage.removeItem(stringKey);
    } catch (_) {}
    try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
    return;
  }

  try {
    if (originalSetItem) originalSetItem.call(window.localStorage, stringKey, stringValue);
    else window.localStorage.setItem(stringKey, stringValue);
    try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
  } catch (err) {
    if (err?.name !== "QuotaExceededError") throw err;
    clearMcStoragePressure();
    try {
      if (originalSetItem) originalSetItem.call(window.localStorage, stringKey, stringValue);
      else window.localStorage.setItem(stringKey, stringValue);
      try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
    } catch (retryErr) {
      if (retryErr?.name !== "QuotaExceededError") throw retryErr;
      if (!writeSessionFallback(stringKey, stringValue) && !isLargeSharedPayloadKey(stringKey) && !isLocalOnlyMcKey(stringKey)) throw retryErr;
    }
  }

  // Deliberately do not dispatch the global storage/sync event and do not queue
  // a cloud write here. This is a local optimistic cache primitive for admin
  // screens that own their own serialized background persistence.
}

export function mcSetItem(key, value) {
  if (typeof window === "undefined") return;
  const stringKey = String(key);
  const stringValue = String(value);
  volatileMcCache.set(stringKey, stringValue);
  if (shouldUseIndexedDb(stringKey, stringValue)) {
    idbSetItem(stringKey, stringValue, { reason: "mc-shared-payload" }).catch((error) => {
      console.warn("Morpheus IndexedDB shared payload write failed", { key: stringKey, error });
    });
    try {
      if (originalRemoveItem) originalRemoveItem.call(window.localStorage, stringKey);
      else window.localStorage.removeItem(stringKey);
    } catch (_) {}
    try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
    scheduleFirestoreWrite(stringKey, stringValue);
    dispatchStorageLikeEvent();
    return;
  }
  let wroteLocal = false;
  try {
    if (originalSetItem) originalSetItem.call(window.localStorage, stringKey, stringValue);
    else window.localStorage.setItem(stringKey, stringValue);
    wroteLocal = true;
    try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
  } catch (err) {
    if (err?.name !== "QuotaExceededError") throw err;
    clearMcStoragePressure();
    try {
      if (isPrioritySharedPayloadKey(stringKey)) {
        if (originalSetItem) originalSetItem.call(window.localStorage, stringKey, stringValue);
        else window.localStorage.setItem(stringKey, stringValue);
        wroteLocal = true;
        try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
      } else if (isLargeSharedPayloadKey(stringKey)) {
        // Full imports/session engines can exceed browser storage. Firestore is
        // the source of truth, so do not crash the UI when local cache is full.
        wroteLocal = writeSessionFallback(stringKey, stringValue);
      } else {
        if (originalSetItem) originalSetItem.call(window.localStorage, stringKey, stringValue);
        else window.localStorage.setItem(stringKey, stringValue);
        wroteLocal = true;
      }
    } catch (retryErr) {
      if (retryErr?.name !== "QuotaExceededError") throw retryErr;
      // Local-only profile/session keys should never crash the access layer.
      // Store them in sessionStorage when the browser's persistent cache is full.
      wroteLocal = writeSessionFallback(stringKey, stringValue);
      if (!wroteLocal && !isLargeSharedPayloadKey(stringKey) && !isLocalOnlyMcKey(stringKey)) throw retryErr;
    }
  }
  scheduleFirestoreWrite(stringKey, stringValue);
  dispatchStorageLikeEvent();
}


export async function mcSetCloudItemIfRevision(key, value, expectedRevision = 0) {
  const stringKey = String(key || '');
  const stringValue = String(value ?? '');
  if (!stringKey || !isMcKey(stringKey) || isLocalOnlyMcKey(stringKey) || !activeOrgId) {
    return { ok: false, conflict: false, currentValue: null, revision: Number(expectedRevision) || 0 };
  }

  const ref = morpheusDocRef(activeOrgId, docIdForLocalKey(stringKey));
  try {
    recentLocalWrites.set(stringKey, Date.now());
    const result = await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(ref);
      const existingData = snapshot.exists() ? (snapshot.data() || {}) : {};
      const currentValue = cloudValueFromDocData(existingData) || null;
      const currentPayload = safeJsonParse(currentValue, {});
      const currentRevision = Number(currentPayload?.revision || 0);
      const expected = Number(expectedRevision || 0);

      if (currentRevision !== expected) {
        return { ok: false, conflict: true, currentValue, revision: currentRevision };
      }

      const nextPayload = safeJsonParse(stringValue, null);
      const nextRevision = currentRevision + 1;
      const nextValue = nextPayload && typeof nextPayload === 'object'
        ? JSON.stringify({ ...nextPayload, revision: nextRevision })
        : stringValue;
      const nextJson = safeJsonParse(nextValue, null);

      transaction.set(ref, {
        localKey: stringKey,
        value: nextValue,
        ...(nextJson !== null ? { json: nextJson } : {}),
        updatedAt: serverTimestamp(),
        updatedByEmail: activeUser.email || '',
      }, { merge: true });

      return { ok: true, conflict: false, currentValue: nextValue, revision: nextRevision };
    });
    return result;
  } catch (err) {
    console.error('Morpheus Count revision-guarded Firestore write failed', { key: stringKey, err });
    return { ok: false, conflict: false, currentValue: null, revision: Number(expectedRevision) || 0, error: err };
  }
}



// Phase 3 — canonical lifecycle authority.
// Complete / Verify are state transitions on the Count Ledger itself, not ordinary
// whole-document saves. Firestore serializes the transition against the latest
// ledger so an older browser can never reopen or replace a terminal count.
export async function mcTransitionCountLedger(key, incomingValue, transition = "complete", actor = {}) {
  const stringKey = String(key || "");
  // Lifecycle owns this key exclusively. Retire any bridge copy left over from a
  // previous render before opening the Firestore transaction.
  scheduledWriteValues.delete(stringKey);
  pendingWrites.delete(stringKey);
  const stringValue = String(incomingValue ?? "");
  const action = String(transition || "").trim().toLowerCase();
  if (!/^mc-count-ledger:/i.test(stringKey) || !activeOrgId) return { ok: false, reason: "invalid-ledger" };

  const ref = morpheusDocRef(activeOrgId, docIdForLocalKey(stringKey));
  try {
    recentLocalWrites.set(stringKey, Date.now());
    const result = await runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(ref);
      const existingValue = snapshot.exists() ? (cloudValueFromDocData(snapshot.data() || {}) || "") : "";
      const incoming = safeJsonParse(stringValue, null);
      const existing = safeJsonParse(existingValue, null);
      const incomingValid = Boolean(incoming && Array.isArray(incoming?.lines));
      const existingValid = Boolean(existing && Array.isArray(existing?.lines));
      if (!incomingValid && !existingValid) return { ok: false, reason: "invalid-payload" };

      // The canonical Firestore ledger is the authority for lifecycle transitions.
      // Finalize/verify must not fail just because the calling browser has evicted,
      // not yet hydrated, or otherwise lacks its local ledger payload. When a valid
      // cloud ledger already exists, transition that document directly. If both
      // sides are valid we still merge field-versioned rows first so the newest
      // acknowledged edit wins before the lifecycle boundary is applied.
      const mergedRaw = incomingValid
        ? (existingValid ? mergeSessionPayloadValues(existingValue, stringValue) : stringValue)
        : existingValue;
      const merged = safeJsonParse(mergedRaw, incomingValid ? incoming : existing);
      const now = new Date().toISOString();
      const currentMeta = { ...(merged?.meta || {}) };
      const alreadyTerminal = isTerminalSessionPayload(existing || {});

      if (action === "finalize-lock") {
        if (!alreadyTerminal && !isTerminalSessionPayload(merged || {})) return { ok: false, reason: "not-complete" };
        const verified = Boolean(
          existing?.meta?.verifiedAt || merged?.meta?.verifiedAt ||
          String(existing?.meta?.verificationStatus || merged?.meta?.verificationStatus || "").toLowerCase() === "verified" ||
          String(existing?.meta?.verification?.status || merged?.meta?.verification?.status || "").toLowerCase() === "verified"
        );
        if (!verified) return { ok: false, reason: "not-verified" };
        currentMeta.status = "Completed";
        currentMeta.lifecycleState = "finalized";
        currentMeta.finalizedAt = existing?.meta?.finalizedAt || currentMeta.finalizedAt || now;
        currentMeta.finalizedBy = existing?.meta?.finalizedBy || currentMeta.finalizedBy || actor?.name || activeUser.email || "Administrator";
        currentMeta.finalizedByProfileId = existing?.meta?.finalizedByProfileId || currentMeta.finalizedByProfileId || actor?.profileId || "";
        currentMeta.immutable = true;
        currentMeta.finalizationRevision = Math.max(Number(existing?.meta?.finalizationRevision || 0), Number(currentMeta.finalizationRevision || 0), Number(currentMeta.lifecycleRevision || 0)) + (existing?.meta?.finalizedAt ? 0 : 1);
        currentMeta.lifecycleEvents = Array.isArray(currentMeta.lifecycleEvents) ? currentMeta.lifecycleEvents : [];
        if (!currentMeta.lifecycleEvents.some((event) => String(event?.type || "").toLowerCase() === "finalized")) {
          currentMeta.lifecycleEvents = [...currentMeta.lifecycleEvents, {
            id: `finalized-${Date.now()}`, type: "finalized", at: currentMeta.finalizedAt,
            actor: currentMeta.finalizedBy, detail: "The canonical count ledger was permanently locked as the authoritative operational result."
          }];
        }
      } else if (action === "verify") {
        if (!alreadyTerminal && !isTerminalSessionPayload(merged || {})) return { ok: false, reason: "not-complete" };
        currentMeta.verificationStatus = "verified";
        currentMeta.verifiedAt = existing?.meta?.verifiedAt || now;
        currentMeta.verifiedBy = actor?.name || currentMeta.verifiedBy || activeUser.email || "Administrator";
        currentMeta.verifiedByProfileId = actor?.profileId || currentMeta.verifiedByProfileId || "";
        currentMeta.verification = {
          ...(currentMeta.verification || {}),
          status: "verified",
          verifiedAt: currentMeta.verifiedAt,
          verifiedBy: currentMeta.verifiedBy,
          verifiedByProfileId: currentMeta.verifiedByProfileId,
        };
      } else if (action === "correct") {
        // MC432 — a submitted correction is a canonical ledger mutation, not a
        // best-effort background save. The incoming corrected rows/meta are the
        // new operational truth and must be committed transactionally before the
        // UI/session list is allowed to advertise the corrected exposure.
        if (!alreadyTerminal && !isTerminalSessionPayload(merged || {})) return { ok: false, reason: "not-complete" };
        if (!incomingValid) return { ok: false, reason: "invalid-correction-payload" };

        // Preserve monotonic lifecycle facts from Firestore while allowing the
        // correction payload to replace row values + correction summary. This is
        // especially important for already-verified restored counts.
        const existingMeta = existing?.meta || {};
        Object.assign(currentMeta, {
          ...(incoming?.meta || {}),
          status: existingMeta.status || incoming?.meta?.status || "Section Complete",
          sectionCompletedAt: existingMeta.sectionCompletedAt || incoming?.meta?.sectionCompletedAt || now,
          completedAt: existingMeta.completedAt || incoming?.meta?.completedAt || existingMeta.sectionCompletedAt || now,
          verificationStatus: existingMeta.verificationStatus || incoming?.meta?.verificationStatus || currentMeta.verificationStatus,
          verifiedAt: existingMeta.verifiedAt || incoming?.meta?.verifiedAt || currentMeta.verifiedAt,
          verifiedBy: existingMeta.verifiedBy || incoming?.meta?.verifiedBy || currentMeta.verifiedBy,
          verifiedByProfileId: existingMeta.verifiedByProfileId || incoming?.meta?.verifiedByProfileId || currentMeta.verifiedByProfileId,
          verification: existingMeta.verification || incoming?.meta?.verification || currentMeta.verification,
          finalizedAt: existingMeta.finalizedAt || incoming?.meta?.finalizedAt || null,
          finalizedBy: existingMeta.finalizedBy || incoming?.meta?.finalizedBy || "",
          finalizedByProfileId: existingMeta.finalizedByProfileId || incoming?.meta?.finalizedByProfileId || "",
          immutable: existingMeta.immutable === true || incoming?.meta?.immutable === true,
          lifecycleState: existingMeta.lifecycleState || incoming?.meta?.lifecycleState || currentMeta.lifecycleState || "section-complete",
        });
      } else {
        // Completion is monotonic and idempotent. Preserve the first terminal time.
        const terminalAt = existing?.meta?.sectionCompletedAt || existing?.meta?.completedAt || now;
        currentMeta.status = action === "finalize" ? "Completed" : "Section Complete";
        currentMeta.sectionCompletedAt = currentMeta.sectionCompletedAt || terminalAt;
        currentMeta.completedAt = currentMeta.completedAt || terminalAt;
        currentMeta.lifecycleRevision = Math.max(Number(existing?.meta?.lifecycleRevision || 0), Number(currentMeta.lifecycleRevision || 0)) + (alreadyTerminal ? 0 : 1);
        currentMeta.lifecycleState = action === "finalize" ? "complete" : "section-complete";
        currentMeta.completedBy = currentMeta.completedBy || actor?.name || activeUser.email || "User";
        currentMeta.completedByProfileId = currentMeta.completedByProfileId || actor?.profileId || "";
      }

      const next = action === "correct"
        ? { ...merged, ...incoming, meta: currentMeta, lines: incoming.lines, savedAt: now }
        : { ...merged, meta: currentMeta, savedAt: now };
      // Firestore rejects nested `undefined` values even when the exact same
      // payload serializes cleanly to JSON. Correction rows can legitimately
      // contain optional fields that are undefined in-memory, so normalize the
      // canonical object through its JSON representation before transaction.set.
      // This keeps `value` and `json` byte-for-byte semantically aligned and
      // prevents correction submissions from failing with
      // "Unsupported field value: undefined".
      const nextValue = JSON.stringify(next);
      const firestoreSafeNext = safeJsonParse(nextValue, {});
      const signalId = docIdForLocalKey(stringKey);
      const shouldSignal = !alreadyTerminal && ['complete','finalize'].includes(action) && Boolean(currentMeta.siteId && currentMeta.cycleId && currentMeta.operationalDate);
      const signalRef = doc(db,'orgs',activeOrgId,'neoMorpheusCompletions',signalId);
      const existingSignal = shouldSignal ? await transaction.get(signalRef) : null;
      if (shouldSignal && !existingSignal.exists()) {
        transaction.set(signalRef,{
          schemaVersion:1,ledgerId:signalId,sessionId:currentMeta.sessionId,
          value:nextValue,completedAt:serverTimestamp(),actorUid:activeUser.uid || '',actorEmail:activeUser.email || '',
        });
      }
      transaction.set(ref, {
        ...(shouldSignal && !existingSignal.exists() ? {neoCompletedAt:serverTimestamp(),neoCompletionSignalId:signalId} : {}),
        localKey: stringKey,
        value: nextValue,
        json: firestoreSafeNext,
        updatedAt: serverTimestamp(),
        updatedByEmail: activeUser.email || "",
        updatedByUid: activeUser.uid || "",
        writeMode: `canonical-lifecycle-${action}-v1`,
      }, { merge: true });
      return { ok: true, payload: next, value: nextValue, alreadyTerminal };
    });
    return result;
  } catch (error) {
    console.error("Morpheus canonical Count Ledger lifecycle transition failed", { key: stringKey, transition: action, error });
    return { ok: false, reason: "firestore-error", error };
  }
}



// Phase 5.2 — immutable historical evidence.
// This is intentionally a separate Firestore document from the operational Count
// Ledger. It is write-once: once a final snapshot exists, no later browser,
// compatibility writer, correction flow, or archive hydration may replace it.
function mcStableStringHash(value = "") {
  const text = String(value ?? "");
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return `fnv1a-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

export async function mcCreateImmutableFinalSnapshot(key, ledgerPayload, actor = {}) {
  const stringKey = String(key || "");
  if (!/^mc-final-count-snapshot:/i.test(stringKey) || !activeOrgId) {
    return { ok: false, reason: "invalid-final-snapshot-key" };
  }

  const incoming = typeof ledgerPayload === "string"
    ? safeJsonParse(ledgerPayload, null)
    : ledgerPayload;
  if (!incoming || !Array.isArray(incoming?.lines)) {
    return { ok: false, reason: "invalid-final-snapshot-payload" };
  }

  const lifecycle = String(incoming?.meta?.lifecycleState || "").trim().toLowerCase();
  const immutable = incoming?.meta?.immutable === true;
  const finalizedAt = String(incoming?.meta?.finalizedAt || "").trim();
  if (lifecycle !== "finalized" || !immutable || !finalizedAt) {
    return { ok: false, reason: "ledger-not-finalized" };
  }

  const ref = morpheusDocRef(activeOrgId, docIdForLocalKey(stringKey));
  const sourceLedgerKey = `mc-count-ledger:${String(incoming?.meta?.sessionId || "").trim()}`;
  const sourceLedgerValue = JSON.stringify(incoming);
  const sourceLedgerDigest = mcStableStringHash(sourceLedgerValue);

  try {
    const result = await runTransaction(db, async (transaction) => {
      const existingSnap = await transaction.get(ref);
      if (existingSnap.exists()) {
        const existingValue = cloudValueFromDocData(existingSnap.data() || {}) || "";
        const existing = safeJsonParse(existingValue, null);
        // Write-once semantics. A repeat finalize is idempotent, but the original
        // historical evidence always wins, even if the caller carries newer data.
        if (existing?.immutable === true && existing?.snapshotType === "final-count") {
          return {
            ok: true,
            existed: true,
            payload: existing,
            value: existingValue,
            digest: existing?.sourceLedgerDigest || "",
          };
        }
        return { ok: false, reason: "final-snapshot-conflict" };
      }

      const capturedAt = new Date().toISOString();
      const snapshotPayload = {
        snapshotType: "final-count",
        snapshotVersion: 1,
        immutable: true,
        sessionId: String(incoming?.meta?.sessionId || "").trim(),
        sourceLedgerKey,
        sourceLedgerRevision: Number(incoming?.revision || incoming?.meta?.finalizationRevision || incoming?.meta?.lifecycleRevision || 0),
        sourceLedgerDigest,
        finalizedAt,
        finalizedBy: incoming?.meta?.finalizedBy || actor?.name || activeUser.email || "Administrator",
        finalizedByProfileId: incoming?.meta?.finalizedByProfileId || actor?.profileId || "",
        capturedAt,
        meta: {
          ...(incoming?.meta || {}),
          immutable: true,
          lifecycleState: "finalized",
          sourceLedgerKey,
          sourceLedgerRevision: Number(incoming?.revision || incoming?.meta?.finalizationRevision || incoming?.meta?.lifecycleRevision || 0),
          sourceLedgerDigest,
          finalSnapshotKey: stringKey,
          finalSnapshotCapturedAt: capturedAt,
        },
        lines: incoming.lines,
        savedAt: incoming?.savedAt || finalizedAt || capturedAt,
      };
      const value = JSON.stringify(snapshotPayload);
      transaction.set(ref, {
        localKey: stringKey,
        value,
        json: snapshotPayload,
        immutable: true,
        snapshotType: "final-count",
        sourceLedgerKey,
        sourceLedgerDigest,
        finalizedAt,
        capturedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedByEmail: activeUser.email || "",
        updatedByUid: activeUser.uid || "",
        writeMode: "immutable-final-count-snapshot-v1",
      });
      return { ok: true, existed: false, payload: snapshotPayload, value, digest: sourceLedgerDigest };
    });
    return result;
  } catch (error) {
    console.error("Morpheus immutable final snapshot write failed", { key: stringKey, error });
    return { ok: false, reason: "firestore-error", error };
  }
}

export async function mcSetCloudItemImmediate(key, value, options = {}) {
  const stringKey = String(key || '');
  const stringValue = String(value ?? '');
  if (!stringKey || !isMcKey(stringKey) || isLocalOnlyMcKey(stringKey)) return false;
  if (!activeOrgId) return false;

  const docId = docIdForLocalKey(stringKey);
  const ref = morpheusDocRef(activeOrgId, docId);
  let lastError = null;

  // Durable user actions (publishing configuration, learning rules, reverting an
  // override) must not report success until Firestore returns the exact value.
  // Retry because an active snapshot listener can briefly replay the previous
  // document while the write acknowledgement is propagating. MC487A records each
  // application retry separately so the dev inspector can distinguish an actual
  // write retry from Firestore transaction callback re-runs and listener echoes.
  const usesTransactionalWriter = isSessionStatePayloadKey(stringKey) || stringKey === "mc-session-list";
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    recordMcTrafficWriteAttempt(stringKey, attempt);
    recentLocalWrites.set(stringKey, Date.now());

    try {
      await writeMcDoc(stringKey, stringValue, { bypassHydrationGate: true });
    } catch (err) {
      lastError = err;
      recordMcTrafficWriteFailure(stringKey, {
        phase: usesTransactionalWriter ? "transaction" : "write",
        attempt,
      });
      await new Promise((resolve) => setTimeout(resolve, attempt * 250));
      continue;
    }

    // MC488 — a resolved Firestore transaction is already a server commit
    // acknowledgement. The Live Count queue was immediately downloading the full
    // Count Ledger again to "verify" that commit; stale comparison results then
    // caused a second full transaction/write for many perfectly successful saves.
    // For canonical Count Ledgers only, trust successful transaction completion.
    // Actual transaction failures still enter the retry loop above, and the local
    // durable queue is removed only after this function returns true.
    // MC497 — `await setDoc()` / a resolved transaction is already a server
    // commit acknowledgement. Some bounded, operator-driven pipelines (notably
    // Global Distribution storage history) can explicitly opt into that contract
    // to avoid downloading the same large document purely to compare it with the
    // value we just wrote. The default remains the stronger read-after-write
    // verification for existing callers.
    const trustServerCommit = options?.trustServerCommit === true;
    if (/^mc-count-ledger:/i.test(stringKey) || trustServerCommit) {
      recentLocalWrites.set(stringKey, Date.now());
      return true;
    }

    try {
      const snapshot = await getDoc(ref);
      const snapshotData = snapshot.exists() ? (snapshot.data() || {}) : null;
      const persistedValue = snapshotData ? cloudValueFromDocData(snapshotData) : null;
      recordMcTrafficFirestoreRead(stringKey, persistedValue || "", "ack", { documentData: snapshotData });
      const acknowledged = stringKey === "mc-session-list"
        ? sessionListCloudValueContainsIncoming(persistedValue, stringValue)
        : isSessionStatePayloadKey(stringKey)
          ? sessionCloudValueContainsIncoming(persistedValue, stringValue)
          : String(persistedValue ?? '') === stringValue;
      if (acknowledged) {
        recentLocalWrites.set(stringKey, Date.now());
        return true;
      }
      recordMcTrafficAckMismatch(stringKey, attempt);
      lastError = new Error(`Firestore acknowledgement mismatch for ${stringKey} (attempt ${attempt}).`);
    } catch (err) {
      lastError = err;
      recordMcTrafficWriteFailure(stringKey, { phase: "ack-read", attempt });
    }
    await new Promise((resolve) => setTimeout(resolve, attempt * 250));
  }

  console.error('Morpheus Count immediate Firestore write failed verification', {
    key: stringKey,
    docId,
    orgId: activeOrgId,
    err: lastError,
  });
  // Explicit distribution writes surface the real Firebase reason (e.g.
  // resource-exhausted / permission-denied) rather than only a generic timeout.
  if (options.throwOnError === true) {
    throw lastError || new Error(`Firestore did not confirm the write for ${stringKey}.`);
  }
  return false;
}

export async function mcSetCloudItem(key, value) {
  const stringKey = String(key || '');
  const stringValue = String(value ?? '');
  if (!stringKey || !isMcKey(stringKey) || isLocalOnlyMcKey(stringKey)) return false;
  if (!activeOrgId) return false;
  if (!activeSnapshotHydrated && !isCriticalSessionKey(stringKey)) {
    pendingWrites.set(stringKey, stringValue);
    recentLocalWrites.set(stringKey, Date.now());
    return true;
  }
  try {
    recentLocalWrites.set(stringKey, Date.now());
    await writeMcDoc(stringKey, stringValue);
    return true;
  } catch (err) {
    recordMcTrafficWriteFailure(stringKey);
    console.error('Morpheus Count direct Firestore write failed', {
      key: stringKey,
      docId: docIdForLocalKey(stringKey),
      orgId: activeOrgId,
      err,
    });
    return false;
  }
}




export async function pruneMorpheusImportDocsForScope(scope = "ALL") {
  const normalized = normalizeImportScopeValue(scope);
  const canonicalEngineDoc = docIdForLocalKey(normalized === "ALL" ? "mc-logic-pass" : `mc-logic-pass:${normalized}`);
  const canonicalImportDoc = docIdForLocalKey(normalized === "ALL" ? "mc-import-active" : `mc-import-active:${normalized}`);

  if (!activeOrgId || !activeSnapshotHydrated) return false;

  // MC491 — never scan the entire org/morpheus collection after an import.
  // Older builds created a small, predictable family of engine/import document
  // IDs from scope aliases. Probe only those legacy IDs, validate their stored
  // localKey/scope, and delete the matching legacy document if it exists.
  // This preserves cleanup compatibility while turning a 1,000+ document scan
  // into a bounded handful of point reads.
  const aliases = Array.from(new Set([
    ...importScopeLockCandidates(normalized),
    normalized,
    normalized.replace(/^inventory_/i, ""),
    String(scope || "ALL"),
    String(scope || "ALL").replace(/^inventory_/i, ""),
  ].map((item) => sanitizeDocId(item)).filter(Boolean)));

  const candidateIds = new Set();
  aliases.forEach((alias) => {
    candidateIds.add(`logic-pass_${alias}`);
    candidateIds.add(`import-active_${alias}`);
    candidateIds.add(`engine_${alias}`);
    candidateIds.add(`import_${alias}`);
  });
  candidateIds.delete(canonicalEngineDoc);
  candidateIds.delete(canonicalImportDoc);
  candidateIds.delete("meta");

  try {
    const cleanupKey = `mc-import-prune:${normalized}`;
    const candidates = Array.from(candidateIds);
    const snapshots = await Promise.all(candidates.map(async (docId) => {
      const ref = doc(db, "orgs", activeOrgId, "morpheus", docId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        recordMcTrafficFirestoreRead(`${cleanupKey}:${docId}`, "", "direct");
        return null;
      }
      const data = snap.data?.() || {};
      recordMcTrafficFirestoreRead(`${cleanupKey}:${docId}`, rawTrafficValue(data), "direct", { documentData: data });
      return { docId, ref, data };
    }));

    const deletions = snapshots.filter(Boolean).map(async ({ docId, ref, data }) => {
      const localKey = localKeyForDoc(docId, data);
      const isEngine = /^mc-logic-pass(?::|$)/i.test(localKey) || /^mc-engine:/i.test(localKey);
      const isImport = /^mc-import-active(?::|$)/i.test(localKey);
      if (!isEngine && !isImport) return false;
      const localScope = normalizeImportScopeValue(normalizeImportScopeFromLocalKey(localKey));
      if (localScope !== normalized) return false;

      recordMcTrafficWriteAttempt(cleanupKey, 1);
      try {
        await deleteDoc(ref);
        recordMcTrafficFirestoreDelete(cleanupKey);
        return true;
      } catch (err) {
        recordMcTrafficWriteFailure(cleanupKey, { phase: "write", attempt: 1 });
        throw err;
      }
    });

    await Promise.all(deletions);
    return true;
  } catch (err) {
    console.warn("Morpheus Count could not prune old import docs", { scope: normalized, err });
    return false;
  }
}

export function mcSubscribeCloudItem(key, callback) {
  const stringKey = String(key || "");
  if (!activeOrgId || !isMcKey(stringKey) || typeof callback !== "function") return () => {};
  const ref = morpheusDocRef(activeOrgId, docIdForLocalKey(stringKey));
  return onSnapshot(ref, (snap) => {
    const trafficMetadata = {
      fromCache: snap.metadata?.fromCache === true,
      hasPendingWrites: snap.metadata?.hasPendingWrites === true,
    };
    if (!snap.exists()) {
      recordMcTrafficFirestoreRead(stringKey, "", "listener", trafficMetadata);
      callback(null);
      return;
    }
    const data = snap.data() || {};
    const value = typeof data.value === "string"
      ? data.value
      : data.json !== undefined
        ? JSON.stringify(data.json)
        : null;
    recordMcTrafficFirestoreRead(stringKey, value || "", "listener", { ...trafficMetadata, documentData: data });
    callback(value, data);
  }, (err) => {
    console.warn("Morpheus Count cloud item subscription failed", { key: stringKey, err });
  });
}

export function mcRemoveItem(key) {
  if (typeof window === "undefined") return;
  const stringKey = String(key);
  volatileMcCache.delete(stringKey);
  try {
    if (originalRemoveItem) originalRemoveItem.call(window.localStorage, stringKey);
    else window.localStorage.removeItem(stringKey);
  } catch (_) {}
  try { window.sessionStorage?.removeItem(stringKey); } catch (_) {}
  idbRemoveItem(stringKey).catch(() => {});
  scheduleFirestoreDelete(stringKey);
  dispatchStorageLikeEvent();
}

export function startMcCloudSync({ orgId = "", userEmail = "", uid = "" } = {}) {
  installLocalStorageBridge();
  const nextOrgId = String(orgId || "").trim();
  activeUser = {
    email: String(userEmail || "").trim().toLowerCase(),
    uid: String(uid || "").trim(),
  };

  if (!nextOrgId) {
    dispatchReady("", false, { lastError: "No orgId resolved for Morpheus Count" });
    return () => {};
  }

  if (unsubscribeSync && activeOrgId === nextOrgId) {
    flushPendingWrites();
    writeMeta();
    const currentState = getMcCloudSyncState();
    const hydratedReady = Boolean(activeSnapshotHydrated && currentState?.ready && currentState?.dataHydrated);
    dispatchReady(nextOrgId, hydratedReady, {
      phase: hydratedReady ? 'ready' : 'hydrating',
      profilesHydrated: Boolean(hydratedReady && currentState?.profilesHydrated),
      hasProfilesDoc: Boolean(currentState?.hasProfilesDoc || currentState?.profilesHydrated),
      dataHydrated: Boolean(hydratedReady && currentState?.dataHydrated),
      docsHydrated: currentState?.docsHydrated || 0,
    });
    return unsubscribeSync;
  }

  if (unsubscribeSync) unsubscribeSync();

  // Only wipe the browser MC cache when the authenticated organisation actually changes
  // or the cache schema is outdated. On a normal page refresh for the same org, keep
  // the already-hydrated profiles/sessions available so the access layer does not
  // sit in a long database-sync loop. This still preserves hard isolation because
  // a different orgId/version forces the same full purge as before.
  let previousOrgId = "";
  let previousCacheVersion = "";
  try { previousOrgId = String(window.sessionStorage?.getItem(ORG_SESSION_KEY) || "").trim(); } catch (_) {}
  try { previousCacheVersion = String(window.sessionStorage?.getItem(ORG_CACHE_VERSION_KEY) || "").trim(); } catch (_) {}
  const sameOrgWarmRefresh = Boolean(previousOrgId && previousOrgId === nextOrgId && previousCacheVersion === ORG_CACHE_VERSION);

  if (!sameOrgWarmRefresh) {
    volatileMcCache.clear();
    clearMcLocalStorageForOrgSwitch(nextOrgId);
  } else {
    try { window.sessionStorage?.setItem(ORG_SESSION_KEY, nextOrgId); } catch (_) {}
    try { window.sessionStorage?.setItem(ORG_CACHE_VERSION_KEY, ORG_CACHE_VERSION); } catch (_) {}
  }

  cleanBootstrapPurgedOrgs.delete(nextOrgId);
  pendingWrites.clear();

  activeOrgId = nextOrgId;
  activeCleanBootstrap = false;
  activeSnapshotHydrated = false;

  // Ownership authority lives on the organisation record in Super Admin. Mirror
  // it into the normal MC sync cache so Directory/profile normalization never
  // relies on an editable contact email to identify the protected owner.
  getDoc(doc(db, "orgs", nextOrgId)).then((orgSnap) => {
    const ownerEmail = String(orgSnap.exists() ? (orgSnap.data()?.primaryOwnerEmail || "") : "").trim().toLowerCase();
    if (!ownerEmail || typeof window === "undefined") return;
    applyingCloud = true;
    try {
      if (originalSetItem) originalSetItem.call(window.localStorage, "mc-primary-owner-email", ownerEmail);
      else window.localStorage.setItem("mc-primary-owner-email", ownerEmail);
    } finally {
      applyingCloud = false;
    }
    window.dispatchEvent(new CustomEvent(MC_SYNC_EVENT, { detail: { orgId: nextOrgId, key: "mc-primary-owner-email" } }));
  }).catch(() => {});

  const trustedWarmProfiles = sameOrgWarmRefresh ? getTrustedCachedProfilesState() : { ok: false, count: 0 };

  // On a normal browser refresh for the same org, reuse the already-hydrated
  // local MC cache immediately. The Firestore listener below still starts and
  // keeps live syncing active, but the Access Layer should not show the long
  // database-sync lock again unless this is a first login, org change, stale
  // cache version, or fallback-admin-only cache.
  dispatchReady(nextOrgId, trustedWarmProfiles.ok, {
    phase: trustedWarmProfiles.ok ? 'ready' : (sameOrgWarmRefresh ? 'warm-cache-verifying' : 'hydrating'),
    warmCache: sameOrgWarmRefresh,
    warmCacheTrusted: trustedWarmProfiles.ok,
    profilesHydrated: trustedWarmProfiles.ok,
    hasProfilesDoc: trustedWarmProfiles.ok,
    dataHydrated: trustedWarmProfiles.ok,
    docsHydrated: trustedWarmProfiles.ok ? trustedWarmProfiles.count : 0,
  });
  writeMeta();

  // COST C3 — Morpheus is ON-DEMAND by default.
  //
  // Historically the app kept a realtime listener on the ENTIRE organisation's
  // `morpheus` collection for the lifetime of the app. That made Archives,
  // configuration, historical ledgers and other non-live data participate in
  // realtime billing even when nobody was using those screens.
  //
  // Hydrate the organisation cache ONCE when Morpheus starts. Features that are
  // genuinely live (active Sessions / count ledgers) own their own narrow
  // document listeners. Everything else refreshes when its page is opened or
  // when the user explicitly refreshes/reloads.
  let syncCancelled = false;
  unsubscribeSync = () => { syncCancelled = true; };

  // MC478 — restore the bounded operational authority set before declaring the
  // org hydrated. This replaces the historical full-collection bootstrap with a
  // deterministic set of small config/current-state reads. Large revisions,
  // snapshots, archives and session details remain page-owned/on-demand.
  Promise.all([
    getDoc(morpheusDocRef(nextOrgId, "profiles")).catch(() => null),
    getDoc(morpheusDocRef(nextOrgId, "mc-profiles")).catch(() => null),
    getDocs(query(
      collection(db, "orgs", nextOrgId, "morpheus"),
      where("localKey", "==", "mc-profiles"),
      limit(5),
    )).catch(() => ({ docs: [] })),
    hydrateBoundedBootstrapAuthorities(nextOrgId),
  ]).then(([profilesSnap, legacyProfilesSnap, legacyProfilesQuery, authorityResult]) => {
    if (syncCancelled || activeOrgId !== nextOrgId) return;

    const docs = [...(authorityResult?.docs || [])];
    if (profilesSnap?.exists?.() && !docs.some((item) => item.id === profilesSnap.id)) docs.push(profilesSnap);
    if (legacyProfilesSnap?.exists?.() && !docs.some((item) => item.id === legacyProfilesSnap.id)) docs.push(legacyProfilesSnap);
    (legacyProfilesQuery?.docs || []).forEach((snap) => {
      if (!docs.some((item) => item.id === snap.id)) docs.push(snap);
    });

    const hydrated = applyMorpheusSnapshotToLocal({ docs }, { replaceCache: false });
    activeSnapshotHydrated = true;
    flushPendingWrites();
    dispatchStorageLikeEvent();

    dispatchReady(nextOrgId, true, {
      phase: "ready",
      profilesHydrated: Boolean(hydrated.hasProfilesDoc || trustedWarmProfiles.ok),
      hasProfilesDoc: Boolean(hydrated.hasProfilesDoc || trustedWarmProfiles.ok),
      hasOrgDataDocs: hydrated.hasOrgDataDocs,
      dataHydrated: true,
      warmCache: sameOrgWarmRefresh,
      warmCacheTrusted: Boolean(trustedWarmProfiles.ok),
      docsHydrated: docs.length,
      onDemandHydration: true,
      boundedAuthorityHydration: true,
      authorityScopes: authorityResult?.scopes || [],
    });
  }).catch((err) => {
    if (syncCancelled || activeOrgId !== nextOrgId) return;
    console.error("Morpheus Count bounded authority hydration failed", { orgId: nextOrgId, err });
    activeSnapshotHydrated = true;
    dispatchReady(nextOrgId, true, {
      phase: "ready",
      profilesHydrated: Boolean(trustedWarmProfiles.ok),
      hasProfilesDoc: Boolean(trustedWarmProfiles.ok),
      dataHydrated: true,
      docsHydrated: 0,
      onDemandHydration: true,
      boundedAuthorityHydration: true,
      lastError: String(err?.message || err),
    });
  });

  return () => {};
}


export async function forceMcCloudSync() {
  if (!activeOrgId) {
    dispatchReady("", false, { lastError: "No orgId resolved for Morpheus Count", phase: "no-org" });
    return { ok: false, reason: "no-org" };
  }

  dispatchReady(activeOrgId, false, { phase: "force-syncing" });

  try {
    const [profilesSnap, legacyProfilesSnap, legacyProfilesQuery] = await Promise.all([
      getDoc(morpheusDocRef(activeOrgId, "profiles")).catch(() => null),
      getDoc(morpheusDocRef(activeOrgId, "mc-profiles")).catch(() => null),
      getDocs(query(
        collection(db, "orgs", activeOrgId, "morpheus"),
        where("localKey", "==", "mc-profiles"),
        limit(5),
      )).catch(() => ({ docs: [] })),
    ]);
    const docs = [];
    if (profilesSnap?.exists?.()) docs.push(profilesSnap);
    if (legacyProfilesSnap?.exists?.()) docs.push(legacyProfilesSnap);
    (legacyProfilesQuery?.docs || []).forEach((snap) => {
      if (!docs.some((item) => item.id === snap.id)) docs.push(snap);
    });
    const snapshot = { docs };
    const hydrated = applyMorpheusSnapshotToLocal(snapshot, { replaceCache: false });
    activeSnapshotHydrated = true;
    dispatchStorageLikeEvent();

    const readyToEnter = true;
    dispatchReady(activeOrgId, readyToEnter, {
      phase: "ready",
      profilesHydrated: hydrated.hasProfilesDoc,
      hasProfilesDoc: hydrated.hasProfilesDoc,
      hasOrgDataDocs: hydrated.hasOrgDataDocs,
      dataHydrated: true,
      docsHydrated: docs.length,
      forced: true,
      accessProfileOnly: true,
    });

    return { ok: readyToEnter, profilesHydrated: hydrated.hasProfilesDoc || true, docsHydrated: snapshot.docs.length };
  } catch (err) {
    console.error("Morpheus Count force sync failed", { orgId: activeOrgId, err });
    dispatchReady(activeOrgId, false, { lastError: String(err?.message || err), phase: "force-sync-failed" });
    return { ok: false, reason: String(err?.message || err) };
  }
}

export function useMcCloudSync({ orgId = "", userEmail = "", uid = "", claims = {} } = {}) {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setReady(false);

    async function run() {
      const resolvedOrgId = String(orgId || (await resolveMcOrgId({ claims, uid, email: userEmail })) || "").trim();
      if (cancelled) return;
      startMcCloudSync({ orgId: resolvedOrgId, userEmail, uid });

      // Safari/iPad can delay or miss the first IndexedDB-backed snapshot.
      // Force a direct Firestore pull shortly after startup so MC profiles,
      // archives and sessions hydrate correctly across Apple devices.
      if (isSafariIOS()) {
        setTimeout(() => {
          forceMcCloudSync().catch(() => {});
        }, 1200);

        setTimeout(() => {
          const state = getMcCloudSyncState();
          if (!state?.profilesHydrated) {
            forceMcCloudSync().catch(() => {});
          }
        }, 3500);
      }
      // Only the Firestore snapshot may mark MC as ready. Resolving an orgId is
      // not enough, because localStorage may still contain only fallback admin
      // data or stale data from another org.
      setReady(false);
    }

    run();

    function onReady(event) {
      if (!cancelled) setReady(!!event?.detail?.ready);
    }
    window.addEventListener(MC_SYNC_READY_EVENT, onReady);

    // MC476 — the cloud sync state is also a durable in-page authority.
    // Do not make the entire Morpheus shell depend on catching one CustomEvent:
    // a fast targeted hydration can complete between React effect setup phases.
    // Reconcile from the current state briefly during bootstrap, then stop.
    let readyChecks = 0;
    const readyStateTimer = window.setInterval(() => {
      if (cancelled) return;
      const state = getMcCloudSyncState();
      const safeReady = Boolean(state?.ready && state?.dataHydrated && state?.phase === "ready");
      if (safeReady) {
        setReady(true);
        window.clearInterval(readyStateTimer);
        return;
      }
      readyChecks += 1;
      if (readyChecks >= 40) window.clearInterval(readyStateTimer);
    }, 250);

    // Cover the case where targeted hydration completed immediately before the
    // event listener above was attached.
    const currentState = getMcCloudSyncState();
    if (currentState?.ready && currentState?.dataHydrated && currentState?.phase === "ready") {
      setReady(true);
      window.clearInterval(readyStateTimer);
    }

    return () => {
      cancelled = true;
      window.clearInterval(readyStateTimer);
      window.removeEventListener(MC_SYNC_READY_EVENT, onReady);
    };
  }, [orgId, userEmail, uid, JSON.stringify(claims || {})]);

  return ready;
}

export { MC_SYNC_EVENT, MC_SYNC_READY_EVENT };

// Dedicated large-import snapshot storage. Raw CSV/POS rows are intentionally
// kept outside the shared Morpheus engine document so a 500+ line inventory
// snapshot cannot exceed Firestore's single-document limit or overwrite
// unrelated organisation state.
//
// Source of truth:
//   scope pointer -> activeImportId -> immutable manifest -> immutable chunks
//
// The Import page never guesses the latest snapshot by timestamp and never
// falls back to an older import after a route remount once the active pointer
// has been committed.
const IMPORT_SNAPSHOT_COLLECTION = "morpheus";
const IMPORT_SNAPSHOT_DOC_PREFIX = "activeSnapshot__";
const IMPORT_SNAPSHOT_CHUNK_CHARS = 320000;

function importSnapshotScopeId(scope = "ALL") {
  return sanitizeDocId(normalizeImportScopeValue(scope) || "ALL");
}

function importSnapshotId(scopeId = "ALL", importId = "") {
  return `${IMPORT_SNAPSHOT_DOC_PREFIX}${scopeId}__IMPORT__${sanitizeDocId(String(importId || "unknown"))}`;
}

function importSnapshotPointerId(scopeId = "ALL") {
  return `${IMPORT_SNAPSHOT_DOC_PREFIX}${scopeId}__ACTIVE`;
}

function isImportSnapshotStorageDoc(docId = "", data = {}) {
  return String(docId || "").startsWith(IMPORT_SNAPSHOT_DOC_PREFIX)
    || ["mcImportSnapshotChunk", "mcImportSnapshotManifest", "mcActiveImportPointer"].includes(String(data?.type || ""));
}

function importTrafficKey(kind = "document", scope = "ALL") {
  const normalized = normalizeImportScopeValue(scope || "ALL");
  return `mc-import-snapshot:${kind}:${normalized}`;
}

function rawTrafficValue(data = {}) {
  if (typeof data?.value === "string") return data.value;
  try { return JSON.stringify(data || {}); } catch (_) { return ""; }
}

function recordImportSnapshotRead(kind, fallbackScope, snap) {
  const data = snap?.exists?.() ? (snap.data?.() || {}) : {};
  const scope = data?.scope || fallbackScope || "ALL";
  recordMcTrafficFirestoreRead(importTrafficKey(kind, scope), rawTrafficValue(data), "direct", { documentData: data });
}

function recordImportSnapshotWrite(kind, scope, data = {}) {
  const key = importTrafficKey(kind, data?.scope || scope || "ALL");
  recordMcTrafficFirestoreWrite(key, rawTrafficValue(data), { documentData: data, jsonValue: null });
}

// Immutable manifest content can be reused between all site aliases. Keep a
// small per-org bounded cache and share concurrent in-flight reads. The active
// pointer is still fetched separately, so switching imports never uses an old
// pointer; this cache holds only content identified by an exact manifest ID.
const manifestContentCache = new Map();
const manifestContentInflight = new Map();
const MAX_MANIFEST_CACHE_ENTRIES = 4;

async function loadImportSnapshotByManifest(collectionRef, manifestId) {
  const originalOrgId = activeOrgId;
  const cacheKey = `${originalOrgId}::${manifestId}`;
  if (manifestContentCache.has(cacheKey)) return manifestContentCache.get(cacheKey);
  if (manifestContentInflight.has(cacheKey)) return manifestContentInflight.get(cacheKey);

  const work = (async () => {
    const manifestSnap = await getDoc(doc(collectionRef, manifestId));
    recordImportSnapshotRead("manifest", "ALL", manifestSnap);
    if (!manifestSnap.exists()) return null;
    const manifest = manifestSnap.data() || {};
    const chunkCount = Number(manifest.chunkCount || 0);
    if (!chunkCount || manifest.status !== "ready") return null;

    const chunkSnaps = await Promise.all(Array.from({ length: chunkCount }, (_, index) =>
      getDoc(doc(collectionRef, `${manifestId}__${String(index).padStart(4, "0")}`))
    ));
    chunkSnaps.forEach((snap) => recordImportSnapshotRead("chunk", manifest.scope || "ALL", snap));
    if (chunkSnaps.some((snap) => !snap.exists())) return null;

    const serialized = chunkSnaps
      .sort((a, b) => Number(a.data()?.index || 0) - Number(b.data()?.index || 0))
      .map((snap) => String(snap.data()?.value || ""))
      .join("");
    const payload = { ...JSON.parse(serialized), snapshotManifest: manifest };
    if (activeOrgId !== originalOrgId) return null;
    manifestContentCache.set(cacheKey, payload);
    while (manifestContentCache.size > MAX_MANIFEST_CACHE_ENTRIES) {
      manifestContentCache.delete(manifestContentCache.keys().next().value);
    }
    return payload;
  })();
  manifestContentInflight.set(cacheKey, work);
  try { return await work; } finally { manifestContentInflight.delete(cacheKey); }
}

export async function saveMorpheusImportSnapshot(scope = "ALL", snapshot = {}, options = {}) {
  if (!activeOrgId) return { ok: false, reason: "no-active-org" };
  const importOrgId = activeOrgId;
  const assertActive = () => {
    options.assertActive?.();
    if (activeOrgId !== importOrgId) throw new Error("Organisation changed during inventory import.");
  };
  assertActive();

  const normalizedScope = normalizeImportScopeValue(scope);
  const scopeId = importSnapshotScopeId(normalizedScope);
  const importId = String(snapshot?.importId || snapshot?.activeImportId || `import-${Date.now()}`);
  const manifestId = importSnapshotId(scopeId, importId);
  const pointerId = importSnapshotPointerId(scopeId);
  const serialized = JSON.stringify({
    ...snapshot,
    scope: normalizedScope,
    importId,
    activeImportId: importId,
  });
  const chunks = [];
  for (let offset = 0; offset < serialized.length; offset += IMPORT_SNAPSHOT_CHUNK_CHARS) {
    chunks.push(serialized.slice(offset, offset + IMPORT_SNAPSHOT_CHUNK_CHARS));
  }

  const collectionRef = collection(db, "orgs", activeOrgId, IMPORT_SNAPSHOT_COLLECTION);
  try {
    // 1. Write immutable chunks for this import.
    await Promise.all(chunks.map(async (value, index) => {
      assertActive();
      const key = importTrafficKey("chunk", normalizedScope);
      const data = {
        type: "mcImportSnapshotChunk",
        scope: normalizedScope,
        importId,
        manifestId,
        index,
        value,
        updatedAt: serverTimestamp(),
      };
      recordMcTrafficWriteAttempt(key, 1);
      try {
        await setDoc(doc(collectionRef, `${manifestId}__${String(index).padStart(4, "0")}`), data);
        recordImportSnapshotWrite("chunk", normalizedScope, { ...data, updatedAt: "<serverTimestamp>" });
      } catch (err) {
        recordMcTrafficWriteFailure(key, { phase: "write", attempt: 1 });
        throw err;
      }
    }));

    assertActive();
    // 2. Mark this exact import ready only after every chunk exists.
    const manifestData = {
      type: "mcImportSnapshotManifest",
      scope: normalizedScope,
      importId,
      manifestId,
      fileName: String(snapshot?.fileName || ""),
      uploadedBy: String(activeUser?.email || activeUser?.uid || ""),
      uploadedByUid: String(activeUser?.uid || ""),
      uploadedAt: String(snapshot?.uploadedAt || new Date().toISOString()),
      rowCount: Number(snapshot?.rowCount ?? snapshot?.rawRows?.length ?? 0),
      columnCount: Number(snapshot?.columnCount ?? snapshot?.parsedHeaders?.length ?? 0),
      fingerprint: String(snapshot?.importFingerprint || ""),
      chunkCount: chunks.length,
      byteLength: serialized.length,
      status: "ready",
      updatedAt: serverTimestamp(),
    };
    recordMcTrafficWriteAttempt(importTrafficKey("manifest", normalizedScope), 1);
    await setDoc(doc(collectionRef, manifestId), manifestData);
    assertActive();
    recordImportSnapshotWrite("manifest", normalizedScope, { ...manifestData, updatedAt: "<serverTimestamp>" });

    // 3. Commit the single authoritative pointer last. Route remounts, CSV,
    // POS sync and future integrations must all resolve through this document.
    const pointerData = {
      type: "mcActiveImportPointer",
      scope: normalizedScope,
      activeImportId: importId,
      manifestId,
      fileName: String(snapshot?.fileName || ""),
      uploadedBy: String(activeUser?.email || activeUser?.uid || ""),
      uploadedByUid: String(activeUser?.uid || ""),
      uploadedAt: String(snapshot?.uploadedAt || new Date().toISOString()),
      rowCount: Number(snapshot?.rowCount ?? snapshot?.rawRows?.length ?? 0),
      columnCount: Number(snapshot?.columnCount ?? snapshot?.parsedHeaders?.length ?? 0),
      fingerprint: String(snapshot?.importFingerprint || ""),
      status: "ready",
      updatedAt: serverTimestamp(),
    };
    recordMcTrafficWriteAttempt(importTrafficKey("pointer", normalizedScope), 1);
    await setDoc(doc(collectionRef, pointerId), pointerData);
    assertActive();
    recordImportSnapshotWrite("pointer", normalizedScope, { ...pointerData, updatedAt: "<serverTimestamp>" });

    return {
      ok: true,
      importId,
      manifestId,
      pointerId,
      chunkCount: chunks.length,
      byteLength: serialized.length,
    };
  } catch (err) {
    console.error("Morpheus Count large import snapshot save failed", { scope, importId, err });
    return { ok: false, reason: "write-failed", err };
  }
}



export async function saveMorpheusImportSnapshotAlias(scope = "ALL", alias = {}, options = {}) {
  if (!activeOrgId) return { ok: false, reason: "no-active-org" };
  options.assertActive?.();

  const normalizedScope = normalizeImportScopeValue(scope);
  const scopeId = importSnapshotScopeId(normalizedScope);
  const pointerId = importSnapshotPointerId(scopeId);
  const sourceManifestId = String(alias?.sourceManifestId || alias?.manifestId || "").trim();
  const sourceImportId = String(alias?.sourceImportId || alias?.activeImportId || alias?.importId || "").trim();
  if (!sourceManifestId || !sourceImportId) return { ok: false, reason: "missing-alias-source" };

  const collectionRef = collection(db, "orgs", activeOrgId, IMPORT_SNAPSHOT_COLLECTION);
  try {
    const pointerData = {
      type: "mcActiveImportAlias",
      scope: normalizedScope,
      status: "ready",
      alias: true,
      sourceScope: String(alias?.sourceScope || "ALL"),
      sourceManifestId,
      sourceImportId,
      activeImportId: String(alias?.aliasImportId || `${sourceImportId}__${scopeId}`),
      inventoryHeader: String(alias?.inventoryHeader || ""),
      rowSiteHeader: String(alias?.rowSiteHeader || ""),
      sourceSiteValue: String(alias?.sourceSiteValue || ""),
      fileName: String(alias?.fileName || ""),
      uploadedAt: String(alias?.uploadedAt || new Date().toISOString()),
      rowCount: Number(alias?.rowCount || 0),
      columnCount: Number(alias?.columnCount || 0),
      distributionBatchId: String(alias?.distributionBatchId || ""),
      distributionOrigin: "ALL_SITES",
      updatedAt: serverTimestamp(),
    };
    recordMcTrafficWriteAttempt(importTrafficKey("pointer", normalizedScope), 1);
    await setDoc(doc(collectionRef, pointerId), pointerData);
    options.assertActive?.();
    recordImportSnapshotWrite("pointer", normalizedScope, { ...pointerData, updatedAt: "<serverTimestamp>" });
    return { ok: true, pointerId, sourceManifestId, sourceImportId };
  } catch (err) {
    console.error("Morpheus Count import snapshot alias save failed", { scope, err });
    return { ok: false, reason: "alias-write-failed", err };
  }
}

export function subscribeMorpheusImportSnapshot(scope = "ALL", onChange = () => {}, onError = () => {}) {
  // COST C1 — active CSV/import snapshots are intentionally ON-DEMAND.
  //
  // Intake / Review / Mapping do not need cross-device realtime behaviour.
  // The active snapshot is already loaded explicitly when those routes mount
  // (and again when the user/browser refreshes). Historically this helper also
  // polled Firestore every 12 seconds so another browser would discover a new
  // CSV automatically. That created recurring reads and large snapshot/chunk
  // downloads for data that may change only a few times per day (or less).
  //
  // Keep the public subscription API as a compatibility no-op so existing
  // callers do not need risky lifecycle changes in this cost sprint. Same-tab
  // writes still update through the established local MC_ACTIVE_INVENTORY_EVENT
  // path; another device/browser intentionally sees a new import on page reload
  // or when that route is opened again.
  void scope;
  void onChange;
  void onError;
  return () => {};
}


export async function loadMorpheusImportSnapshotsForScopes(scopes = []) {
  if (!activeOrgId) return {};

  const requested = Array.from(new Set(
    (Array.isArray(scopes) ? scopes : [])
      .map((scope) => String(scope || "").trim())
      .filter(Boolean)
  ));
  if (!requested.length) return {};

  const collectionRef = collection(db, "orgs", activeOrgId, IMPORT_SNAPSHOT_COLLECTION);
  const pointerEntries = await Promise.all(requested.map(async (requestedScope) => {
    const normalizedScope = normalizeImportScopeValue(requestedScope);
    const scopeId = importSnapshotScopeId(normalizedScope);
    const pointerId = importSnapshotPointerId(scopeId);
    try {
      const pointerSnap = await getDoc(doc(collectionRef, pointerId));
      recordImportSnapshotRead("pointer", normalizedScope, pointerSnap);
      if (!pointerSnap.exists()) return { requestedScope, normalizedScope, pointer: null };
      const pointer = pointerSnap.data() || {};
      if (pointer.status !== "ready") return { requestedScope, normalizedScope, pointer: null };
      const manifestId = String(
        pointer.sourceManifestId ||
        pointer.manifestId ||
        importSnapshotId(scopeId, pointer.activeImportId)
      ).trim();
      if (!manifestId) return { requestedScope, normalizedScope, pointer: null };
      return { requestedScope, normalizedScope, pointer: { ...pointer, manifestId } };
    } catch (err) {
      console.warn("Morpheus Count active import pointer batch read failed", { requestedScope, err });
      return { requestedScope, normalizedScope, pointer: null };
    }
  }));

  const manifestIds = Array.from(new Set(
    pointerEntries.map((entry) => entry.pointer?.manifestId).filter(Boolean)
  ));
  const manifestPairs = await Promise.all(manifestIds.map(async (manifestId) => {
    try {
      return [manifestId, await loadImportSnapshotByManifest(collectionRef, manifestId)];
    } catch (err) {
      console.warn("Morpheus Count active import manifest batch read failed", { manifestId, err });
      return [manifestId, null];
    }
  }));
  const manifests = new Map(manifestPairs);

  const result = {};
  pointerEntries.forEach((entry) => {
    const pointer = entry.pointer;
    const active = pointer ? manifests.get(pointer.manifestId) : null;
    if (!active) return;
    const inventoryHeader = String(pointer.inventoryHeader || "");
    result[entry.requestedScope] = {
      ...active,
      scope: entry.normalizedScope,
      importId: String(pointer.activeImportId || active.importId || ""),
      activeImportId: String(pointer.activeImportId || active.activeImportId || active.importId || ""),
      mapping: {
        ...(active.mapping || {}),
        ...(inventoryHeader ? { systemSoh: inventoryHeader } : {}),
      },
      distributionContext: pointer.alias ? {
        ...(active.distributionContext || {}),
        mode: "SITE",
        scope: entry.normalizedScope,
        inheritedFrom: String(pointer.sourceScope || "ALL"),
        rowSiteHeader: String(pointer.rowSiteHeader || ""),
        sourceSiteValue: String(pointer.sourceSiteValue || ""),
        distributionBatchId: String(pointer.distributionBatchId || ""),
      } : (active.distributionContext || {}),
      activeImportPointer: pointer,
      activeImportAlias: Boolean(pointer.alias),
    };
  });
  return result;
}

export async function loadMorpheusImportSnapshot(scope = "ALL") {
  if (!activeOrgId) return null;

  const normalizedScope = normalizeImportScopeValue(scope);
  const scopeId = importSnapshotScopeId(normalizedScope);
  const pointerId = importSnapshotPointerId(scopeId);
  const collectionRef = collection(db, "orgs", activeOrgId, IMPORT_SNAPSHOT_COLLECTION);

  try {
    // Active pointer is the only authority for new snapshots.
    const pointerSnap = await getDoc(doc(collectionRef, pointerId));
    recordImportSnapshotRead("pointer", normalizedScope, pointerSnap);
    if (pointerSnap.exists()) {
      const pointer = pointerSnap.data() || {};
      const manifestId = String(pointer.sourceManifestId || pointer.manifestId || importSnapshotId(scopeId, pointer.activeImportId));
      if (pointer.status === "ready" && manifestId) {
        const active = await loadImportSnapshotByManifest(collectionRef, manifestId);
        if (active) {
          if (pointer.alias) {
            const inventoryHeader = String(pointer.inventoryHeader || "");
            return {
              ...active,
              scope: normalizedScope,
              importId: String(pointer.activeImportId || active.importId || ""),
              activeImportId: String(pointer.activeImportId || active.activeImportId || active.importId || ""),
              mapping: { ...(active.mapping || {}), ...(inventoryHeader ? { systemSoh: inventoryHeader } : {}) },
              distributionContext: {
                ...(active.distributionContext || {}),
                mode: "SITE",
                scope: normalizedScope,
                inheritedFrom: String(pointer.sourceScope || "ALL"),
                rowSiteHeader: String(pointer.rowSiteHeader || ""),
                sourceSiteValue: String(pointer.sourceSiteValue || ""),
                distributionBatchId: String(pointer.distributionBatchId || ""),
              },
              activeImportPointer: pointer,
              activeImportAlias: true,
            };
          }
          return { ...active, activeImportPointer: pointer };
        }
      }
    }

    // Backward-compatible migration path for snapshots created before the
    // active-pointer architecture. Once loaded, callers can continue working;
    // the next CSV/POS snapshot will establish the pointer automatically.
    const legacy = await loadImportSnapshotByManifest(collectionRef, scopeId);
    return legacy;
  } catch (err) {
    console.warn("Morpheus Count active import snapshot load failed", { scope, err });
    return null;
  }
}

// A bounded, on-demand catalogue for Neo's scope selector. No inventory listener.
const neoCatalogueSynced = new Map();
export async function mcSyncNeoCatalogue(catalogue) {
  const orgId = activeOrgId;
  if (!orgId || !activeUser.uid) return;
  const raw = JSON.stringify(catalogue);
  const key = `${orgId}|${catalogue.siteId}`;
  if (neoCatalogueSynced.get(key) === raw) return;
  const call = httpsCallable(getFunctions(undefined,'us-central1'),'neoMorpheus');
  const result = await call({orgId,action:'syncCatalogue',catalogue});
  if (activeOrgId === orgId && result.data?.ok) neoCatalogueSynced.set(key,raw);
}
